import { jsPDF } from 'jspdf';
import autoTable, { type CellHook, type RowInput } from 'jspdf-autotable';
import HuttonsWhiteLogo from '../../img/Huttons_white.png';
import { savePdfDocument } from './savePdfDocument';
import {
  calculateBsd,
  getAbsdRate,
  getEffectiveCitizenshipForAbsd,
  CITIZENSHIP_LABELS,
  type CitizenshipType,
  type PropertyCount,
} from '../../utils/stampDutyRates';

// ── Color palette (matches affordabilityPdfExport.ts / mortgageLoanPdfExport.ts) ──
const ORANGE_RGB: [number, number, number] = [255, 152, 0];
const DARK_BG_RGB: [number, number, number] = [15, 23, 42];
const FOOTER_BG_RGB: [number, number, number] = [20, 30, 48];
const INFO_BOX_BG_RGB: [number, number, number] = [30, 41, 59];

const SUMMARY_BAR_NAVY_RGB: [number, number, number] = [15, 27, 58];
const SECTION_HEADER_BLUE_RGB: [number, number, number] = [46, 84, 148]; // #2e5494
const ZEBRA_GRAY_RGB: [number, number, number] = [245, 247, 249];
const LABEL_GRAY_RGB: [number, number, number] = [102, 102, 102];
const VALUE_GOLD_RGB: [number, number, number] = [255, 214, 102];
const ALERT_RED_RGB: [number, number, number] = [184, 62, 45];

// ── Types ───────────────────────────────────────────────────────────────────

export interface AgentInfo {
  Name?: string;
  CEANumber?: string;
  Mobile?: string;
  Email?: string;
  Photo?: string;
}

export interface BuyingBorrowerPdf {
  age: number;
  monthlyIncome: number;
  otherAnnualIncome: number;
  isSelfEmployed: boolean;
  existingMonthlyDebt: number;
}

/** A single milestone in the payment schedule (progressive / resale / HDB). */
export interface BuyingScheduleRowPdf {
  label: string;
  date?: string;
  /** e.g. "5%" or "15%" — already formatted */
  percentLabel?: string;
  payment?: number;
  loan?: number;
  cash?: number;
  cpf?: number;
  bridging?: number;
  cumulativeLoan?: number;
  monthlyInstallment?: number;
}

export interface BuyingPdfInputs {
  mode?: 'simple' | 'detailed';
  propertyTab: 'private' | 'ec' | 'hdb';
  privateSubTab: 'new-launch' | 'resale';
  ecPaymentScheme: 'NPS' | 'DPS';

  purchasePrice: number;
  hasBankLoan: boolean;
  ltvDecimal: number;
  interestRate: number;
  tenure: number;
  legalFee: number;
  spDate: string; // formatted display string

  citizenship: CitizenshipType;
  propertyCount: PropertyCount;
  ftaEligible: boolean;

  // Funds
  additionalCash: number;
  cashProceedFromSelling: number;
  cpf: number;
  additionalCpf: number;

  // CPF payment toggles (mirror on-screen Initial Outlay)
  bsdByCpf: boolean;
  absdByCpf: boolean;
  legalFeeByCpf: boolean;

  /** Joint-purchase ABSD rate (IRAS joint tables incl. married-couple remission).
   *  When set, overrides the primary-borrower rate derived from citizenship/count. */
  absdRatePctOverride?: number;

  // Borrowers
  primary: BuyingBorrowerPdf;
  includeAdditionalBorrower: boolean;
  additional: BuyingBorrowerPdf;

  // Additional-borrower stamp duty profile (only meaningful when joint)
  additionalCitizenship: CitizenshipType;
  additionalPropertyCount: PropertyCount;
  additionalFtaEligible: boolean;

  // HDB-specific (only relevant when propertyTab === 'hdb')
  hdbLoanSource?: 'hdb' | 'bank' | 'none';
  hdbValuationPrice?: number;
  hdbLoanAmount?: number;
  hdbInterestRate?: number;
  hdbLegalFee?: number;
  hdbValuationFee?: number;
  hdbAdminFee?: number;
  hdbHpsAmount?: number;
  hdbHousingGrant?: number;
  hdbCpfResaleGrant?: number;
  hdbPhgGrant?: number;
}

export interface BuyingPdfResults {
  maxLoan: number;
  maxPriceLoan: number;
  derivedLoan: number;
  loanShortfall: number;
  availableTDSR: number;
  totalRecognisedIncome: number;
  totalExistingDebt: number;
  stressTestRate: number;
  effectivePledged: number;
  effectiveUnpledged: number;
}

export interface InvestmentTimelineStage {
  label: string;          // e.g. "10% FOUNDATION"
  periodLabel: string;    // e.g. "6 MTHS  Nov-26"
  cashCpf: number;        // buyer-funded portion this stage
  /** CPF portion of cashCpf (cash = cashCpf − cpf). Omitted ⇒ fully cash. */
  cpf?: number;
  loanPct: number;        // % of price disbursed by bank this stage
  loanAmount: number;     // $ disbursed by bank this stage
  /** Special Bridging Loan disbursed this stage (EC DPS, at TOP). */
  bridging?: number;
  monthlyRepayment: number;
}

/** Per-borrower row for the timeline page's Borrower Profile table. */
export interface TimelineBorrower {
  /** Profile name — shown as the column header instead of "Borrower N" when set. */
  name?: string;
  age: number;
  selfEmployed: boolean;
  /** Gross monthly income. */
  income: number;
  /** Assessable income after MAS haircuts (self-employed 70%, variable 70%). */
  recognisedIncome: number;
  otherAnnualIncome: number;
  existingMonthlyDebt: number;
  monthlyCpfOa: number;
  /** Max loan this borrower qualifies for alone (TDSR/MSR at stress rate). */
  loanEligibility?: number;
  /** Max purchase price this borrower supports alone (eligible loan ÷ max LTV). */
  maxPurchasePrice?: number;
}

export interface InvestmentTimelineData {
  bucPrice: number;
  // Top timeline milestone dates (formatted display strings)
  bookingDate: string;
  spReceiveDate: string;
  exerciseDate: string;
  stampDutyDate: string;
  pay15Date: string;
  launchDate: string;
  foundationDate: string;
  /**
   * HDB resale only. The HDB schedule has one more milestone than the
   * progressive one, so Submit Resale Application gets its own slot instead of
   * sharing the generic ones above.
   */
  hdbResaleAppDate?: string;
  hdbResaleAppAmount?: number;
  // Top timeline amounts
  otpAmount: number;          // 5%
  cancellationPenalty: number; // 1.25%
  refundable: number;          // 3.75%
  bsd: number;
  absd: number;
  pay15Amount: number;         // 15%
  sum20PlusSd: number;         // 20% + stamp duty
  foundationTopUp: number;     // buyer-funded at foundation
  additionalTopUp: number;     // extra cash/CPF for lower LTV
  totalCashCpf: number;
  // CPF portions of each buyer-funded milestone (cash = milestone total − cpf).
  // When omitted the milestone is treated as fully cash. Drives the timeline
  // cards' "CASH DUE / CPF DUE" split.
  otpCpf?: number;             // CPF within the OTP / option-fee payment
  pay15Cpf?: number;           // CPF within the 15% (or resale exercise) payment
  stampDutyCpf?: number;       // CPF within BSD + ABSD
  foundationCpf?: number;      // CPF within the foundation / completion top-up
  // Simple mode: the must-be-cash portion of the S&P (15%) payment. When > 0 (the 55% LTV
  // / 10% regime) the S&P timeline card splits into a Cash line + a CPF/Cash line.
  pay15MandatoryCash?: number;
  // Home Loan Estimates
  ageIncomeRows: { age: number; salary: number }[];
  /** Per-borrower profile for the Borrower Profile table (falls back to ageIncomeRows). */
  borrowers?: TimelineBorrower[];
  iwaa: number;
  maxLoanTenure: number;
  interestRate: number;
  monthlyCpfOa: number;
  totalMonthlyDebt: number;
  /** Total recognised monthly income used in the TDSR assessment. */
  totalIncome?: number;
  ltvPct: number;
  ltvLoan: number;
  estLtvPct: number;     // income-based max loan as % of price
  estLtvLoan: number;    // = maxLoan
  maxPropertyPrice: number;
  minSalaryTdsr: number;
  /** Only set when MSR applies (EC). Omitted for private — MSR is not assessed. */
  minSalaryMsr?: number;
  shortfallLtvPct: number;
  shortfallLtvAmt: number;
  unpledge: number;
  pledge: number;
  // Progressive payment estimates
  stages: InvestmentTimelineStage[];
  // Simple mode: the buyer's cash/CPF split is unknown, so milestone titles use the
  // "Booking Fee" / "Pay 15%" wording and flexible milestones are labelled
  // "CPF/CASH DUE" rather than "CASH DUE".
  simpleMode?: boolean;
}

export interface FundSummaryData {
  // Initial Outlay (cash/CPF/bridging split per row) + fund pledging.
  // `mandatoryCash` (simple mode): the must-be-cash portion of the full row amount; the
  // remainder is the flexible portion rendered in the "CPF/CASH" column.
  outlayRows: { label: string; cpf: number; cash: number; bridging?: number; mandatoryCash?: number }[];
  hasCpfCol: boolean;
  hasBridgingCol?: boolean;
  totalOutlayBridging?: number;
  totalOutlayCpf: number;
  totalOutlayCash: number;
  // Simple mode: sum of the mandatory-cash portions (the rest of the cash total is CPF/Cash).
  totalMandatoryCash?: number;
  pledged: number;
  unpledged: number;
  grandTotalCash: number;   // total outlay cash + pledged + unpledged
  // Fund Balance
  availableCpf: number;
  availableCash: number;
  progDpCpf: number;        // buyer-funded progressive down payment
  progDpCash: number;
  balanceCpf: number;
  balanceCash: number;
  // Reserves (mirror the on-screen two boxes)
  monthlyInstallment: number;
  monthlyCpfOa: number;
  monthsCurrent: number | null;   // Infinity ⇒ sustainable, null ⇒ no loan
  monthsEmployed: number | null;
  /** Special Bridging Loan (EC DPS, buyer owns an HDB flat). Omitted ⇒ not applicable. */
  specialBridging?: {
    valuation: number;
    outstandingLoan: number;
    levy: number;
    maxLoan: number;   // 90% × valuation − outstanding loan − levy
    applied: number;   // amount disbursed at TOP (capped at the TOP tranche)
  };
}

export interface BuyingPdfData {
  inputs: BuyingPdfInputs;
  results: BuyingPdfResults;
  /** Optional last-page "Project Investment Timeline" (new-launch / EC only). */
  investmentTimeline?: InvestmentTimelineData;
  /** Initial Outlay + Fund Balance recap shown on the timeline page. */
  fundSummary?: FundSummaryData;
  /** Down-payment / progressive stages used by ResultsSummary (private new-launch & EC). */
  computedStages?: BuyingScheduleRowPdf[];
  /**
   * Progressive purchases: the installment once the loan is fully drawn (outstanding
   * balance over the tenure left at the last drawdown). Overrides the full-loan,
   * full-tenure figure. Omitted for resale / HDB.
   */
  completionInstallment?: number;
  /** Full payment schedule rows to render in the Schedule section. */
  scheduleRows?: BuyingScheduleRowPdf[];
  /**
   * Milestones merged on the on-screen Progressive Schedule, as contiguous groups of
   * schedule-row indices (e.g. `[[0,1,2,3,4,5,6],[7],[8]]` when the agent merged the
   * first seven stages). Each group prints as one row with its amounts summed, so the
   * report shows the same milestones the agent set up. Omitted = no merges.
   */
  scheduleGroups?: number[][];
  profileName?: string;
  primaryBorrowerName?: string;
  additionalBorrowerName?: string;
  agentInfo?: AgentInfo;
}

// ── Helpers ───────────────────────────────────────────────────────────────

/**
 * The merge groups that apply to a `rowCount`-row schedule, or null when they don't
 * describe it — a stale grouping (row count changed since the schedule reported it) or
 * one with nothing actually merged both fall back to the unmerged schedule.
 */
const validScheduleGroups = (groups: number[][] | undefined, rowCount: number): number[][] | null => {
  if (!groups || groups.length === 0) return null;
  const total = groups.reduce((a, g) => a + g.length, 0);
  if (total !== rowCount || !groups.some((g) => g.length > 1)) return null;
  if (groups.some((g) => g.some((i) => i < 0 || i >= rowCount))) return null;
  return groups;
};

/**
 * Collapses each merged group into a single schedule row — amounts summed, the first
 * milestone's label and date kept — mirroring the on-screen merged stage card
 * ("OTP (Option Fee 5%) + 6 more"). `numberLabel` spans the group ("1-7").
 */
const mergeScheduleRows = (
  rows: BuyingScheduleRowPdf[],
  groups: number[][] | undefined,
): { row: BuyingScheduleRowPdf; numberLabel: string }[] => {
  const valid = validScheduleGroups(groups, rows.length);
  if (!valid) return rows.map((row, i) => ({ row, numberLabel: `${i + 1}` }));
  return valid.map((g) => {
    const members = g.map((i) => rows[i]);
    const first = members[0];
    // Undefined stays undefined when no member carries the figure, so a column the
    // schedule leaves blank (e.g. Loan on the upfront milestones) prints blank.
    const sum = (pick: (r: BuyingScheduleRowPdf) => number | undefined) => {
      const vals = members.map(pick).filter((v): v is number => v !== undefined);
      return vals.length > 0 ? vals.reduce((a, v) => a + v, 0) : undefined;
    };
    return {
      numberLabel: g.length > 1 ? `${g[0] + 1}-${g[g.length - 1] + 1}` : `${g[0] + 1}`,
      row: {
        ...first,
        label: g.length > 1 ? `${first.label} + ${g.length - 1} more` : first.label,
        payment: sum((r) => r.payment),
        loan: sum((r) => r.loan),
        cash: sum((r) => r.cash),
        cpf: sum((r) => r.cpf),
        bridging: sum((r) => r.bridging),
      },
    };
  });
};

const loadImageEl = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const t = setTimeout(() => reject(new Error('timeout')), 10000);
    img.onload = () => { clearTimeout(t); resolve(img); };
    img.onerror = () => { clearTimeout(t); reject(new Error('failed')); };
    img.src = url;
  });

const loadUserPhoto = async (doc: jsPDF, agent: AgentInfo, photoX: number, photoY: number, radius: number): Promise<boolean> => {
  if (!agent.Photo) return false;
  try {
    const photoPath = agent.Photo.startsWith('/') ? agent.Photo.substring(1) : agent.Photo;
    const photoUrl = `https://portal.huttonsgroup.com/${photoPath}`;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    return await new Promise<boolean>((resolve) => {
      const timeout = setTimeout(() => resolve(false), 5000);
      img.onload = () => {
        clearTimeout(timeout);
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (ctx) {
            const size = radius * 2;
            const px = Math.max(256, Math.round(size * 12));
            canvas.width = px;
            canvas.height = px;
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            const r = px / 2;
            const maxInCircle = r * Math.SQRT2;
            const scale = Math.min(maxInCircle / img.width, maxInCircle / img.height);
            const sw = Math.round(img.width * scale);
            const sh = Math.round(img.height * scale);
            ctx.beginPath();
            ctx.arc(r, r, r, 0, Math.PI * 2);
            ctx.closePath();
            ctx.clip();
            ctx.drawImage(img, (px - sw) / 2, (px - sh) / 2, sw, sh);
            const dataUrl = canvas.toDataURL('image/png');
            doc.addImage(dataUrl, 'PNG', photoX - radius, photoY - radius, size, size);
            resolve(true);
          } else {
            resolve(false);
          }
        } catch {
          resolve(false);
        }
      };
      img.onerror = () => { clearTimeout(timeout); resolve(false); };
      img.src = photoUrl;
    });
  } catch {
    return false;
  }
};

const formatCurrency = (value: number): string =>
  `$${Math.round(value).toLocaleString('en-SG', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const signedCurrency = (value: number): string =>
  (value < 0 ? `-${formatCurrency(Math.abs(value))}` : formatCurrency(value));

const formatPct = (value: number, decimals = 2): string => `${value.toFixed(decimals)}%`;

// Lowercase roman numeral for the timeline milestone badges (i, ii, iii …).
// Only ever asked for single-digit milestone counts, so the map stops at x.
const romanNumeral = (n: number): string => {
  const map: [number, string][] = [[10, 'x'], [9, 'ix'], [5, 'v'], [4, 'iv'], [1, 'i']];
  let rest = Math.max(1, Math.round(n));
  let out = '';
  map.forEach(([v, sym]) => {
    while (rest >= v) { out += sym; rest -= v; }
  });
  return out;
};

const calcMonthlyInstallment = (principal: number, annualRate: number, tenureYears: number): number => {
  if (principal <= 0 || tenureYears <= 0) return 0;
  const r = annualRate / 100 / 12;
  const n = tenureYears * 12;
  if (r === 0) return principal / n;
  const factor = Math.pow(1 + r, n);
  return principal * (r * factor) / (factor - 1);
};

const PROPERTY_COUNT_LABELS: Record<PropertyCount, string> = {
  '1st': '1st Property',
  '2nd': '2nd Property',
  '3rd+': '≥ 3rd Property',
};

const propertyTypeLabel = (i: BuyingPdfInputs): string => {
  if (i.mode === 'simple') return 'Simple Buying Estimate';
  if (i.propertyTab === 'hdb') return 'HDB';
  if (i.propertyTab === 'ec') return `Executive Condo (${i.ecPaymentScheme})`;
  return i.privateSubTab === 'resale' ? 'Private (Resale)' : 'Private (New Launch)';
};

// ── Outlay computation (faithful port of ResultsSummary in BuyingCalculator) ──
interface OutlayResult {
  totalLoan: number;
  monthlyInstallment: number;
  ltvPct: string;
  bsd: number;
  absdAmount: number;
  absdRatePct: number;
  dpStages: BuyingScheduleRowPdf[];
  useStagedDp: boolean;
  dpCpf: number;
  dpCash: number;
  bsdCpf: number;
  bsdCash: number;
  absdCpf: number;
  absdCash: number;
  legalFeeCpf: number;
  legalFeeCash: number;
  hasCpfCol: boolean;
  totalCash: number;
  totalCpfUsed: number;
  cashAvailable: number;
  cpfAvailable: number;
  cashBalance: number;
  cpfBalance: number;
  reserveMonths: number;
}

const computeOutlay = (data: BuyingPdfData): OutlayResult => {
  const i = data.inputs;
  const price = i.purchasePrice;
  const ltvDecimal = i.ltvDecimal;
  const totalLoan = Math.round(price * (i.hasBankLoan ? ltvDecimal : 0));
  const minCash = Math.round(price * 0.05);
  const totalDown = price - totalLoan;
  const cpfUsed = i.cpf + i.additionalCpf;
  const cashUsed = minCash + i.additionalCash;

  const bsd = calculateBsd(price);
  const effectiveCitizenship = getEffectiveCitizenshipForAbsd(i.citizenship, i.ftaEligible);
  // Joint purchases pass the IRAS joint-table rate (incl. married-couple remission).
  const absdRatePct = i.absdRatePctOverride ?? getAbsdRate(effectiveCitizenship, i.propertyCount);
  const absdAmount = Math.round((price * absdRatePct) / 100);

  const cpfAfterDown = Math.max(0, cpfUsed - (totalDown - cashUsed));
  const bsdCpf = i.bsdByCpf ? Math.min(bsd, cpfAfterDown) : 0;
  const bsdCash = bsd - bsdCpf;
  const cpfAfterBsd = Math.max(0, cpfAfterDown - bsdCpf);
  const absdCpf = i.absdByCpf ? Math.min(absdAmount, cpfAfterBsd) : 0;
  const absdCash = absdAmount - absdCpf;
  // Legal fee is all-or-nothing: paid entirely by CPF when opted in, else fully cash.
  const legalFeeCpf = i.legalFeeByCpf ? i.legalFee : 0;
  const legalFeeCash = i.legalFee - legalFeeCpf;
  const totalStampDutyCpf = bsdCpf + absdCpf;
  const totalStampDutyCash = bsdCash + absdCash;

  const monthlyInstallment = data.completionInstallment ?? calcMonthlyInstallment(totalLoan, i.interestRate, i.tenure);
  const ltvPct = (ltvDecimal * 100).toFixed(1);

  const stagedDownPayment =
    i.mode === 'simple' || (i.propertyTab === 'private' && i.privateSubTab === 'new-launch') || i.propertyTab === 'ec';
  const allStages = data.computedStages ?? [];
  const dpStages = stagedDownPayment ? allStages : [];
  const useStagedDp = dpStages.length > 0;
  const dpCpf = useStagedDp ? dpStages.reduce((a, s) => a + (s.cpf ?? 0), 0) : 0;
  const dpCash = useStagedDp ? dpStages.reduce((a, s) => a + (s.cash ?? 0), 0) : Math.round(price * 0.05);

  const hasCpfCol = i.bsdByCpf || i.absdByCpf || i.legalFeeByCpf || dpCpf > 0;

  const totalCash = dpCash + totalStampDutyCash + legalFeeCash;
  const totalCpfUsed = dpCpf + totalStampDutyCpf + legalFeeCpf;

  const cashAvailable = i.additionalCash + i.cashProceedFromSelling;
  const cpfAvailable = i.cpf + i.additionalCpf;
  const cashBalance = cashAvailable - totalCash;
  const cpfBalance = cpfAvailable - totalCpfUsed;
  const reserveFunds = Math.max(0, cashBalance) + Math.max(0, cpfBalance);
  const reserveMonths = monthlyInstallment > 0 ? Math.floor(reserveFunds / monthlyInstallment) : 0;

  return {
    totalLoan, monthlyInstallment, ltvPct, bsd, absdAmount, absdRatePct,
    dpStages, useStagedDp, dpCpf, dpCash, bsdCpf, bsdCash, absdCpf, absdCash,
    legalFeeCpf, legalFeeCash, hasCpfCol, totalCash, totalCpfUsed,
    cashAvailable, cpfAvailable, cashBalance, cpfBalance, reserveMonths,
  };
};

// ── Header (pages 2+): slim navy bar. Page indicator stamped in a final pass. ──
const addReportHeader = (doc: jsPDF, title: string) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFillColor(...SUMMARY_BAR_NAVY_RGB);
  doc.rect(0, 0, pageWidth, 10, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(title.toUpperCase(), 12, 6.5);
  doc.setTextColor(0, 0, 0);
};

// ── Four-column summary bar (navy, gold loan) ─────────────────────────────
const drawFourColumnSummaryBar = (doc: jsPDF, data: BuyingPdfData, outlay: OutlayResult, yStart: number): number => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 12;
  const barW = pageWidth - margin * 2;
  const barH = 26;
  const colW = barW / 4;
  const r = 2;

  doc.setFillColor(...SUMMARY_BAR_NAVY_RGB);
  doc.roundedRect(margin, yStart, barW, barH, r, r, 'F');

  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.15);
  for (let c = 1; c <= 3; c++) {
    const x = margin + c * colW;
    doc.line(x, yStart + 3, x, yStart + barH - 3);
  }

  const i = data.inputs;
  const drawCol = (colIndex: number, label: string, value: string, valueGold: boolean, sub?: string) => {
    const cx = margin + colIndex * colW + colW / 2;
    doc.setTextColor(220, 225, 235);
    doc.setFontSize(6.2);
    doc.setFont('helvetica', 'bold');
    doc.text(label, cx, yStart + 6.5, { align: 'center' });
    doc.setFontSize(10.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...(valueGold ? VALUE_GOLD_RGB : [255, 255, 255] as [number, number, number]));
    doc.text(value, cx, yStart + 14, { align: 'center' });
    if (sub) {
      doc.setTextColor(220, 225, 235);
      doc.setFontSize(6);
      doc.setFont('helvetica', 'normal');
      doc.text(sub, cx, yStart + 20.5, { align: 'center' });
    }
    doc.setTextColor(0, 0, 0);
  };

  drawCol(0, 'PURCHASE PRICE', formatCurrency(i.purchasePrice), false);
  drawCol(1, 'BANK LOAN', formatCurrency(outlay.totalLoan), true, i.hasBankLoan ? `LTV ${outlay.ltvPct}%` : 'No bank loan');
  drawCol(2, 'MONTHLY INSTALLMENT', formatCurrency(outlay.monthlyInstallment), false, `${i.interestRate.toFixed(2)}% p.a.`);
  drawCol(3, 'LOAN TENURE', `${i.tenure} yrs`, false, propertyTypeLabel(i));

  return yStart + barH + 5;
};

const sectionHeadRow = (title: string, colSpan = 2) => [
  {
    content: title.toUpperCase(),
    colSpan,
    styles: {
      fillColor: SECTION_HEADER_BLUE_RGB,
      textColor: [255, 255, 255] as [number, number, number],
      fontStyle: 'bold' as const,
      fontSize: 8.5,
      cellPadding: { top: 4, bottom: 4, left: 8, right: 8 },
      halign: 'left' as const,
    },
  },
];

// ── Footer (page-center number stamped in a final pass) ───────────────────
const addFooter = (doc: jsPDF, logoDataUrl?: string, logoAspect?: number, agentInfo?: AgentInfo) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const footerY = pageHeight - 15;
  doc.setFillColor(...FOOTER_BG_RGB);
  doc.rect(0, footerY, pageWidth, 15, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  if (logoDataUrl && logoAspect) {
    const logoH = 7;
    const logoW = logoH * logoAspect;
    doc.addImage(logoDataUrl, 'PNG', 10, footerY + (15 - logoH) / 2, logoW, logoH);
  } else {
    doc.text('Huttons', 10, footerY + 8);
  }
  if (agentInfo?.Name) {
    doc.setTextColor(...ORANGE_RGB);
    doc.setFont('helvetica', 'bold');
    doc.text(agentInfo.Name, pageWidth - 10, footerY + 5, { align: 'right' });
    if (agentInfo.CEANumber) {
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'normal');
      doc.text(`CEA: ${agentInfo.CEANumber}`, pageWidth - 10, footerY + 11, { align: 'right' });
    }
  } else {
    doc.setTextColor(200, 200, 200);
    doc.text('Buying Report', pageWidth - 10, footerY + 8, { align: 'right' });
  }
  doc.setTextColor(0, 0, 0);
};

/** Final pass: write "PAGE x / total" in each report header and the centered
 *  page number in each footer, once the true page count is known. */
const stampPageNumbers = (doc: jsPDF) => {
  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    // Per-page dimensions (the timeline page may be landscape).
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    // Centered footer page number (all pages)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text(`${p}`, pageWidth / 2, pageHeight - 7, { align: 'center' });
    // Top-right page indicator (report pages only — page 1 is the cover)
    if (p > 1) {
      doc.setFontSize(7.5);
      doc.setTextColor(220, 225, 235);
      doc.text(`PAGE ${p} / ${total}`, pageWidth - 12, 6.5, { align: 'right' });
    }
    doc.setTextColor(0, 0, 0);
  }
};

const zebraPairTableDefaults = {
  theme: 'plain' as const,
  styles: { font: 'helvetica', fontSize: 9, cellPadding: { top: 3.4, bottom: 3.4, left: 8, right: 8 } },
  columnStyles: {
    0: { font: 'helvetica', fontStyle: 'normal' as const, textColor: LABEL_GRAY_RGB as [number, number, number] },
    1: {
      font: 'helvetica',
      fontStyle: 'bold' as const,
      textColor: [0, 0, 0] as [number, number, number],
      halign: 'right' as const,
    },
  },
  alternateRowStyles: { fillColor: ZEBRA_GRAY_RGB as [number, number, number] },
};

/** Tight section header for half-width paired tables (matches Maximizer layout). */
const sectionHeadRowTight = (title: string) => [
  {
    content: title.toUpperCase(),
    colSpan: 2,
    styles: {
      fillColor: SECTION_HEADER_BLUE_RGB,
      textColor: [255, 255, 255] as [number, number, number],
      fontStyle: 'bold' as const,
      fontSize: 7.5,
      cellPadding: { top: 3, bottom: 3, left: 6, right: 6 },
      halign: 'left' as const,
    },
  },
];

/** Compact zebra table used for side-by-side (half-width) sections. */
const pairTableDefaults = {
  theme: 'plain' as const,
  pageBreak: 'avoid' as const,
  rowPageBreak: 'avoid' as const,
  styles: {
    font: 'helvetica',
    fontSize: 7.5,
    cellPadding: { top: 2.6, bottom: 2.6, left: 6, right: 6 },
    overflow: 'linebreak' as const,
  },
  columnStyles: {
    0: {
      font: 'helvetica',
      fontStyle: 'normal' as const,
      textColor: LABEL_GRAY_RGB as [number, number, number],
      halign: 'left' as const,
    },
    1: {
      font: 'helvetica',
      fontStyle: 'bold' as const,
      textColor: [0, 0, 0] as [number, number, number],
      halign: 'right' as const,
    },
  },
  alternateRowStyles: { fillColor: ZEBRA_GRAY_RGB as [number, number, number] },
};

// ── Cover page ─────────────────────────────────────────────────────────────
const renderCoverPage = async (doc: jsPDF, data: BuyingPdfData, logoDataUrl?: string, logoAspect?: number) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const agent = data.agentInfo;

  doc.setFillColor(...DARK_BG_RGB);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');
  doc.setFillColor(...ORANGE_RGB);
  doc.rect(0, 0, pageWidth, 8, 'F');

  // Landscape pages have ~87mm less height — tighten the vertical rhythm so the
  // full cover (logo → title → agent card → client → disclaimer) still fits.
  const compact = pageHeight < 250;

  let yPos = compact ? 24 : 28;
  if (logoDataUrl && logoAspect) {
    const logoH = 12;
    const logoW = logoH * logoAspect;
    doc.addImage(logoDataUrl, 'PNG', (pageWidth - logoW) / 2, yPos - logoH + 2, logoW, logoH);
  } else {
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('HUTTONS', pageWidth / 2, yPos, { align: 'center' });
  }
  yPos += compact ? 8 : 10;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(200, 200, 200);
  doc.text("SINGAPORE'S LARGEST PRIVATE REAL ESTATE AGENCY", pageWidth / 2, yPos, { align: 'center' });

  yPos += compact ? 8 : 10;
  doc.setFillColor(...ORANGE_RGB);
  doc.rect(pageWidth / 2 - 45, yPos, 90, 1.2, 'F');

  yPos += compact ? 18 : 22;
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('BUYING REPORT', pageWidth / 2, yPos, { align: 'center' });
  yPos += compact ? 9 : 10;
  doc.setTextColor(...ORANGE_RGB);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(propertyTypeLabel(data.inputs), pageWidth / 2, yPos, { align: 'center' });
  yPos += compact ? 12 : 16;

  // Agent info card
  if (agent) {
    const infoBoxW = 120;
    const infoBoxH = compact ? 46 : 50;
    const infoBoxX = (pageWidth - infoBoxW) / 2;
    const infoBoxY = yPos;

    doc.setFillColor(...INFO_BOX_BG_RGB);
    doc.setDrawColor(...ORANGE_RGB);
    doc.setLineWidth(2);
    doc.roundedRect(infoBoxX, infoBoxY, infoBoxW, infoBoxH, 3, 3, 'FD');

    const photoRadius = 18;
    const photoPadding = 10;
    const photoX = infoBoxX + photoPadding + photoRadius;
    const photoY = infoBoxY + infoBoxH / 2;

    const photoLoaded = await loadUserPhoto(doc, agent, photoX, photoY, photoRadius);
    if (!photoLoaded) {
      doc.setFillColor(...ORANGE_RGB);
      doc.circle(photoX, photoY, photoRadius, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      const initials = agent.Name
        ? agent.Name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase()
        : 'U';
      doc.text(initials, photoX, photoY + 3, { align: 'center' });
    }

    const textX = infoBoxX + photoPadding + photoRadius * 2 + 15;
    let detailY = infoBoxY + 12;
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    if (agent.Name) { doc.text(agent.Name, textX, detailY); detailY += 7; }
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    if (agent.CEANumber) {
      const label = 'CEA Registration: ';
      doc.setTextColor(255, 255, 255);
      doc.text(label, textX, detailY);
      doc.setTextColor(...ORANGE_RGB);
      doc.setFont('helvetica', 'bold');
      doc.text(agent.CEANumber, textX + doc.getTextWidth(label), detailY);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'normal');
      detailY += 6;
    }
    if (agent.Email) { doc.text(agent.Email, textX, detailY); detailY += 6; }
    if (agent.Mobile) { doc.text(agent.Mobile, textX, detailY); }

    yPos = infoBoxY + infoBoxH + (compact ? 10 : 14);
  } else {
    yPos += 8;
  }

  // Client name
  if (data.profileName && data.profileName.trim() !== '') {
    yPos += compact ? 8 : 10;
    doc.setTextColor(200, 200, 200);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Specially Prepared for', pageWidth / 2, yPos, { align: 'center' });
    yPos += 9;
    doc.setTextColor(...ORANGE_RGB);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(data.profileName.trim(), pageWidth / 2, yPos, { align: 'center' });
    yPos += compact ? 10 : 12;
  }

  const genDate = new Date().toLocaleDateString('en-SG', { year: 'numeric', month: 'long', day: 'numeric' });
  doc.setTextColor(200, 200, 200);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated on: ${genDate}`, pageWidth / 2, yPos, { align: 'center' });

  yPos += compact ? 8 : 10;
  doc.setFillColor(...ORANGE_RGB);
  doc.rect(pageWidth / 2 - 40, yPos, 80, 0.8, 'F');

  yPos += compact ? 8 : 10;
  doc.setTextColor(160, 160, 160);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'italic');
  const disclaimer = 'This report is for informational purposes only. Actual costs and loan terms may vary. Subject to MAS regulations, IRAS stamp duty rules and lender policies.';
  doc.text(doc.splitTextToSize(disclaimer, pageWidth - 50), pageWidth / 2, yPos, { align: 'center' });

  addFooter(doc, logoDataUrl, logoAspect, agent);
};

// ── Details / results page(s) ────────────────────────────────────────────
const MARGIN = 12;

const renderDetailsPages = (
  doc: jsPDF,
  data: BuyingPdfData,
  outlay: OutlayResult,
  logoDataUrl?: string,
  logoAspect?: number,
): void => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const innerW = pageWidth - MARGIN * 2;
  const i = data.inputs;
  const r = data.results;
  const joint = i.includeAdditionalBorrower;

  const newPage = (withBar: boolean): number => {
    doc.addPage();
    addReportHeader(doc, 'Buying report');
    addFooter(doc, logoDataUrl, logoAspect, data.agentInfo);
    let y = 13;
    if (withBar) y = drawFourColumnSummaryBar(doc, data, outlay, y);
    return y;
  };

  const ensureSpace = (y: number, needed: number): number => {
    if (y + needed > pageHeight - 20) {
      return newPage(false);
    }
    return y;
  };

  const lastY = (): number =>
    (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;

  // Two-column geometry for side-by-side (Maximizer-style) sections.
  const gap = 4;
  const halfW = (innerW - gap) / 2;

  interface PairCol {
    title: string;
    body: string[][];
    /** label-column width as a fraction of the half-width column */
    labelFrac?: number;
    didParseCell?: CellHook;
  }

  /** Render two key-value tables side by side at the same Y; returns the lower finalY. */
  const renderPairRow = (left: PairCol, right: PairCol, startY: number): number => {
    const pairPage = doc.getCurrentPageInfo().pageNumber;
    autoTable(doc, {
      startY,
      head: [sectionHeadRowTight(left.title)],
      body: left.body,
      ...pairTableDefaults,
      columnStyles: {
        ...pairTableDefaults.columnStyles,
        0: { ...pairTableDefaults.columnStyles[0], cellWidth: halfW * (left.labelFrac ?? 0.5) },
      },
      margin: { left: MARGIN, right: pageWidth - MARGIN - halfW },
      tableWidth: halfW,
      didParseCell: left.didParseCell,
    });
    const leftBottom = lastY();

    doc.setPage(pairPage);
    autoTable(doc, {
      startY,
      head: [sectionHeadRowTight(right.title)],
      body: right.body,
      ...pairTableDefaults,
      columnStyles: {
        ...pairTableDefaults.columnStyles,
        0: { ...pairTableDefaults.columnStyles[0], cellWidth: halfW * (right.labelFrac ?? 0.5) },
      },
      margin: { left: MARGIN + halfW + gap, right: MARGIN },
      tableWidth: halfW,
      didParseCell: right.didParseCell,
    });
    return Math.max(leftBottom, lastY());
  };

  // ── Page A ──
  let yPos = newPage(true);

  // ── Build section bodies ──
  const effCitizenship = getEffectiveCitizenshipForAbsd(i.citizenship, i.ftaEligible);
  const propLoanRows: string[][] = [
    ['Property Type', propertyTypeLabel(i)],
    ['Purchase Price', formatCurrency(i.purchasePrice)],
    ['Citizenship', CITIZENSHIP_LABELS[i.citizenship] + (i.ftaEligible ? ' (FTA)' : '')],
    ['Property Count', PROPERTY_COUNT_LABELS[i.propertyCount]],
    ['Bank Loan', i.hasBankLoan ? `Yes (LTV ${outlay.ltvPct}%)` : 'No'],
    ['Loan Amount', formatCurrency(outlay.totalLoan)],
    ['Interest Rate', `${formatPct(i.interestRate)} p.a.`],
    ['Loan Tenure', `${i.tenure} years`],
    // Blank when the agent hid timeline dates — drop the row rather than show an empty value.
    ...(i.spDate ? [[i.propertyTab === 'hdb' ? 'OTP Date' : 'OTP / S&P Date', i.spDate]] : []),
  ];
  if (effCitizenship !== i.citizenship) {
    propLoanRows.splice(4, 0, ['Effective Citizenship (ABSD)', CITIZENSHIP_LABELS[effCitizenship]]);
  }

  const borrowerRows: string[][] = [
    ['Age', `${i.primary.age} years`],
    ['Monthly Income', formatCurrency(i.primary.monthlyIncome)],
    ['Other Annual Income', formatCurrency(i.primary.otherAnnualIncome)],
    ['Self-Employed', i.primary.isSelfEmployed ? 'Yes' : 'No'],
    ['Existing Monthly Debt', formatCurrency(i.primary.existingMonthlyDebt)],
  ];

  const shortfallRow: string[] = r.loanShortfall > 0
    ? ['Loan Shortfall', formatCurrency(r.loanShortfall)]
    : ['Loan Shortfall', 'None'];
  const affordabilityRows: string[][] = [
    ['Max Loan Quantum', formatCurrency(r.maxLoan)],
    ['Max Purchase Price (by Loan)', r.maxPriceLoan > 0 ? formatCurrency(r.maxPriceLoan) : '–'],
    ['Required Loan', formatCurrency(r.derivedLoan)],
    shortfallRow,
    ['Total Recognised Income (Monthly)', formatCurrency(r.totalRecognisedIncome)],
    ['Existing Monthly Debt', formatCurrency(r.totalExistingDebt)],
    ['Available TDSR (Monthly)', formatCurrency(r.availableTDSR)],
    [i.propertyTab === 'hdb' && i.hdbLoanSource === 'hdb' ? 'HDB Interest Rate Floor' : 'Stress Test Rate', `${formatPct(r.stressTestRate)} p.a.`],
  ];
  const shortfallRowIdx = affordabilityRows.findIndex((row) => row === shortfallRow);
  const affordabilityHook: CellHook = (hookData) => {
    if (
      hookData.section === 'body'
      && hookData.column.index === 1
      && hookData.row.index === shortfallRowIdx
      && r.loanShortfall > 0
    ) {
      hookData.cell.styles.textColor = ALERT_RED_RGB;
    }
  };

  const stampRows: string[][] = [
    ['BSD (Buyer\'s Stamp Duty)', formatCurrency(outlay.bsd)],
  ];
  if (outlay.absdAmount > 0) {
    stampRows.push([`ABSD (${outlay.absdRatePct}%)`, formatCurrency(outlay.absdAmount)]);
  } else {
    stampRows.push(['ABSD', 'None']);
  }
  stampRows.push(['Total Stamp Duty', formatCurrency(outlay.bsd + outlay.absdAmount)]);
  stampRows.push(['Legal / Conveyancing Fee', formatCurrency(i.legalFee)]);

  const pairHeight = (leftRows: number, rightRows: number): number =>
    9 + Math.max(leftRows, rightRows) * 5.6 + 6;

  // ── Row 1: Property & Loan Details | Borrower ──
  if (joint) {
    // Joint borrowers use a 3-column table that needs full width; keep it stacked.
    autoTable(doc, {
      startY: yPos,
      head: [sectionHeadRow('Property & Loan Details')],
      body: propLoanRows,
      ...zebraPairTableDefaults,
      margin: { left: MARGIN, right: MARGIN },
      tableWidth: innerW,
    });
    yPos = lastY() + 5;

    const primaryLabel = data.primaryBorrowerName?.trim() || 'Primary';
    const additionalLabel = data.additionalBorrowerName?.trim() || 'Additional';
    autoTable(doc, {
      startY: yPos,
      head: [[
        { content: 'BORROWER', styles: { halign: 'left' as const } },
        { content: primaryLabel, styles: { halign: 'right' as const } },
        { content: additionalLabel, styles: { halign: 'right' as const } },
      ]],
      body: [
        ['Age', `${i.primary.age} years`, `${i.additional.age} years`],
        ['Monthly Income', formatCurrency(i.primary.monthlyIncome), formatCurrency(i.additional.monthlyIncome)],
        ['Other Annual Income', formatCurrency(i.primary.otherAnnualIncome), formatCurrency(i.additional.otherAnnualIncome)],
        ['Self-Employed', i.primary.isSelfEmployed ? 'Yes' : 'No', i.additional.isSelfEmployed ? 'Yes' : 'No'],
        ['Existing Monthly Debt', formatCurrency(i.primary.existingMonthlyDebt), formatCurrency(i.additional.existingMonthlyDebt)],
      ],
      theme: 'plain',
      styles: { font: 'helvetica', fontSize: 9, cellPadding: { top: 3.4, bottom: 3.4, left: 8, right: 8 } },
      headStyles: {
        fillColor: SECTION_HEADER_BLUE_RGB,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8.5,
        cellPadding: { top: 4, bottom: 4, left: 8, right: 8 },
      },
      columnStyles: {
        0: { textColor: LABEL_GRAY_RGB, halign: 'left' },
        1: { fontStyle: 'bold', textColor: [0, 0, 0], halign: 'right' },
        2: { fontStyle: 'bold', textColor: [0, 0, 0], halign: 'right' },
      },
      alternateRowStyles: { fillColor: ZEBRA_GRAY_RGB },
      margin: { left: MARGIN, right: MARGIN },
      tableWidth: innerW,
    });
    yPos = lastY() + 5;
  } else {
    yPos = ensureSpace(yPos, pairHeight(propLoanRows.length, borrowerRows.length));
    yPos = renderPairRow(
      { title: 'Property & Loan Details', body: propLoanRows, labelFrac: 0.5 },
      { title: 'Borrower', body: borrowerRows, labelFrac: 0.5 },
      yPos,
    ) + 5;
  }

  // ── Row 2: Loan Affordability | Stamp Duty & Legal Fees ──
  yPos = ensureSpace(yPos, pairHeight(affordabilityRows.length, stampRows.length));
  yPos = renderPairRow(
    { title: 'Loan Affordability', body: affordabilityRows, labelFrac: 0.62, didParseCell: affordabilityHook },
    { title: 'Stamp Duty & Legal Fees', body: stampRows, labelFrac: 0.6 },
    yPos,
  ) + 5;

  // Income support (only when there is a shortfall and amounts provided)
  if (r.loanShortfall > 0 && (r.effectivePledged > 0 || r.effectiveUnpledged > 0)) {
    yPos = ensureSpace(yPos, 32);
    autoTable(doc, {
      startY: yPos,
      head: [sectionHeadRow('Income Support to Bridge Shortfall')],
      body: [
        ['Unpledged Amount (liquid assets)', formatCurrency(r.effectiveUnpledged)],
        ['Pledged Amount (locked-in funds)', formatCurrency(r.effectivePledged)],
      ],
      ...zebraPairTableDefaults,
      margin: { left: MARGIN, right: MARGIN },
      tableWidth: innerW,
    });
    yPos = lastY() + 5;
  }

  // ── Initial Outlay (CPF / Cash / Total) ──
  yPos = ensureSpace(yPos, 70);
  const hasCpf = outlay.hasCpfCol;
  const outlayHead: { content: string; styles: { halign: 'left' | 'right' } }[] = [
    { content: 'INITIAL OUTLAY', styles: { halign: 'left' } },
  ];
  if (hasCpf) outlayHead.push({ content: 'CPF', styles: { halign: 'right' } });
  outlayHead.push({ content: 'Cash', styles: { halign: 'right' } });
  if (hasCpf) outlayHead.push({ content: 'Total', styles: { halign: 'right' } });

  const moneyCell = (v?: number): string => (v !== undefined ? formatCurrency(v) : '');
  const outRow = (label: string, cpf?: number, cash?: number): string[] => {
    const row = [label];
    if (hasCpf) row.push(moneyCell(cpf));
    row.push(moneyCell(cash));
    if (hasCpf) row.push((cpf !== undefined || cash !== undefined) ? formatCurrency((cpf ?? 0) + (cash ?? 0)) : '');
    return row;
  };

  const outlayBody: string[][] = [];
  // Down payment
  if (outlay.useStagedDp) {
    outlay.dpStages.forEach((s) => {
      outlayBody.push(outRow(s.label, s.cpf && s.cpf > 0 ? s.cpf : undefined, s.cash));
    });
  } else {
    outlayBody.push(outRow('Min Cash (5%)', undefined, outlay.dpCash));
  }
  // Stamp duty
  outlayBody.push(outRow('BSD', i.bsdByCpf ? outlay.bsdCpf : undefined, i.bsdByCpf ? outlay.bsdCash : outlay.bsd));
  if (outlay.absdAmount > 0) {
    outlayBody.push(outRow(`ABSD (${outlay.absdRatePct}%)`, i.absdByCpf ? outlay.absdCpf : undefined, i.absdByCpf ? outlay.absdCash : outlay.absdAmount));
  }
  // Legal fee
  outlayBody.push(outRow('Legal / Conveyancing', i.legalFeeByCpf ? outlay.legalFeeCpf : undefined, i.legalFeeByCpf ? outlay.legalFeeCash : i.legalFee));
  // Total
  outlayBody.push(outRow('Total Initial Outlay', hasCpf ? outlay.totalCpfUsed : undefined, outlay.totalCash));

  const totalRowIndex = outlayBody.length - 1;
  const numCols = outlayHead.length;

  // ── Fund Balance config ──
  const fbHasCpf = outlay.cpfAvailable > 0 || outlay.totalCpfUsed > 0;
  const fbHead: { content: string; styles: { halign: 'left' | 'right' } }[] = [
    { content: 'FUND BALANCE', styles: { halign: 'left' } },
  ];
  if (fbHasCpf) fbHead.push({ content: 'CPF', styles: { halign: 'right' } });
  fbHead.push({ content: 'Cash', styles: { halign: 'right' } });

  const fbRow = (label: string, cpf: number, cash: string, signed = false): string[] => {
    const row = [label];
    if (fbHasCpf) row.push(signed ? signedCurrency(cpf) : formatCurrency(cpf));
    row.push(cash);
    return row;
  };
  const fbBody: string[][] = [
    fbRow('Available Funds', outlay.cpfAvailable, formatCurrency(outlay.cashAvailable)),
    fbRow('Less: Initial Outlay', -outlay.totalCpfUsed, signedCurrency(-outlay.totalCash), true),
    fbRow('Balance', outlay.cpfBalance, signedCurrency(outlay.cashBalance), true),
  ];
  const fbTotalRowIdx = fbBody.length - 1;
  // Monthly installment & reserves live inside the Fund Balance table (value in the last column).
  const reservesText = outlay.monthlyInstallment > 0 ? `${outlay.reserveMonths} month${outlay.reserveMonths === 1 ? '' : 's'}` : '–';
  const fbNoteRow = (label: string, value: string): string[] => (fbHasCpf ? [label, '', value] : [label, value]);
  fbBody.push(fbNoteRow('Monthly Installment', formatCurrency(outlay.monthlyInstallment)));
  fbBody.push(fbNoteRow('Reserves — Months Covered', reservesText));
  const fbNumCols = fbHead.length;

  // Shared styling for the side-by-side multi-column tables.
  const wideBodyStyles = {
    font: 'helvetica',
    fontSize: 8,
    cellPadding: { top: 3, bottom: 3, left: 6, right: 6 },
    valign: 'middle' as const,
  };
  const wideHeadStyles = {
    fillColor: SECTION_HEADER_BLUE_RGB,
    textColor: [255, 255, 255] as [number, number, number],
    fontStyle: 'bold' as const,
    fontSize: 8,
    cellPadding: { top: 4, bottom: 4, left: 6, right: 6 },
  };
  const wideColStyles = (cols: number) => Object.fromEntries(
    Array.from({ length: cols }, (_, c) => [
      c,
      c === 0
        ? { textColor: LABEL_GRAY_RGB, halign: 'left' as const }
        : { fontStyle: 'bold' as const, textColor: [0, 0, 0] as [number, number, number], halign: 'right' as const },
    ]),
  );

  // ── Initial Outlay | Fund Balance (side by side) ──
  // Compact font + tight padding + fixed money-column widths so the CPF/Cash/Total
  // figures stay on one line within the half-width column (labels may wrap instead).
  const resultBodyStyles = { ...wideBodyStyles, fontSize: 7, cellPadding: { top: 2.6, bottom: 2.6, left: 3, right: 3 } };
  const resultHeadStyles = { ...wideHeadStyles, fontSize: 7, cellPadding: { top: 3.5, bottom: 3.5, left: 3, right: 3 } };
  const resultColStyles = (cols: number, moneyW: number) => Object.fromEntries(
    Array.from({ length: cols }, (_, c) => [
      c,
      c === 0
        ? { textColor: LABEL_GRAY_RGB, halign: 'left' as const }
        : { fontStyle: 'bold' as const, textColor: [0, 0, 0] as [number, number, number], halign: 'right' as const, cellWidth: moneyW, overflow: 'visible' as const },
    ]),
  );

  yPos = ensureSpace(yPos, 16 + Math.max(outlayBody.length, fbBody.length) * 7);
  const resultStartY = yPos;
  const resultPage = doc.getCurrentPageInfo().pageNumber;
  autoTable(doc, {
    startY: resultStartY,
    head: [outlayHead],
    body: outlayBody,
    theme: 'plain',
    styles: resultBodyStyles,
    headStyles: resultHeadStyles,
    columnStyles: resultColStyles(numCols, hasCpf ? 19 : 26),
    alternateRowStyles: { fillColor: ZEBRA_GRAY_RGB },
    margin: { left: MARGIN, right: pageWidth - MARGIN - halfW },
    tableWidth: halfW,
    didParseCell: (hookData) => {
      if (hookData.section === 'body' && hookData.row.index === totalRowIndex) {
        hookData.cell.styles.fontStyle = 'bold';
        hookData.cell.styles.textColor = ORANGE_RGB;
      }
    },
  });
  const outlayBottom = lastY();

  doc.setPage(resultPage);
  autoTable(doc, {
    startY: resultStartY,
    head: [fbHead],
    body: fbBody,
    theme: 'plain',
    styles: resultBodyStyles,
    headStyles: resultHeadStyles,
    columnStyles: resultColStyles(fbNumCols, fbHasCpf ? 24 : 30),
    alternateRowStyles: { fillColor: ZEBRA_GRAY_RGB },
    margin: { left: MARGIN + halfW + gap, right: MARGIN },
    tableWidth: halfW,
    didParseCell: (hookData) => {
      if (hookData.section === 'body' && hookData.row.index === fbTotalRowIdx) {
        hookData.cell.styles.fontStyle = 'bold';
        if (hookData.column.index === 1 && fbHasCpf && outlay.cpfBalance < 0) hookData.cell.styles.textColor = ALERT_RED_RGB;
        if (hookData.column.index === fbNumCols - 1 && outlay.cashBalance < 0) hookData.cell.styles.textColor = ALERT_RED_RGB;
      }
    },
  });
  yPos = Math.max(outlayBottom, lastY()) + 5;

  // ── Payment Schedule ──
  // Milestones merged on screen print as one row here too (amounts summed).
  const sched = mergeScheduleRows(data.scheduleRows ?? [], data.scheduleGroups);
  if (sched.length > 0) {
    yPos = ensureSpace(yPos, 50);
    // Dates are omitted entirely when the agent hid them on screen — drop the column
    // rather than print a blank one.
    const hasDateCol = sched.some(({ row }) => (row.date ?? '').trim() !== '');
    // Special Bridging Loan (EC DPS) funds part of the TOP milestone.
    const hasBridgingCol = sched.some(({ row }) => (row.bridging ?? 0) > 0);
    const head = [[
      { content: '#', styles: { halign: 'center' as const } },
      { content: 'Milestone', styles: { halign: 'left' as const } },
      ...(hasDateCol ? [{ content: 'Date', styles: { halign: 'left' as const } }] : []),
      { content: 'Payment', styles: { halign: 'right' as const } },
      { content: 'Loan', styles: { halign: 'right' as const } },
      ...(hasBridgingCol ? [{ content: 'Bridging', styles: { halign: 'right' as const } }] : []),
      { content: 'Cash/CPF', styles: { halign: 'right' as const } },
    ]];
    const body = sched.map(({ row: s, numberLabel }) => [
      numberLabel,
      s.label,
      ...(hasDateCol ? [s.date ?? ''] : []),
      s.payment !== undefined ? formatCurrency(s.payment) : '',
      s.loan !== undefined ? formatCurrency(s.loan) : '',
      ...(hasBridgingCol ? [(s.bridging ?? 0) > 0 ? formatCurrency(s.bridging ?? 0) : ''] : []),
      ((s.cash ?? 0) + (s.cpf ?? 0)) > 0 ? formatCurrency((s.cash ?? 0) + (s.cpf ?? 0)) : '',
    ]);
    autoTable(doc, {
      startY: yPos,
      head,
      body,
      theme: 'plain',
      styles: { font: 'helvetica', fontSize: 8, cellPadding: { top: 2.6, bottom: 2.6, left: 5, right: 5 }, valign: 'middle' },
      headStyles: {
        fillColor: SECTION_HEADER_BLUE_RGB,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
        cellPadding: { top: 3.5, bottom: 3.5, left: 5, right: 5 },
        valign: 'middle',
      },
      columnStyles: {
        0: { textColor: LABEL_GRAY_RGB, halign: 'center', cellWidth: 11, cellPadding: { top: 2.6, bottom: 2.6, left: 1, right: 1 } },
        1: { textColor: [0, 0, 0], halign: 'left' },
        ...(hasDateCol ? { 2: { textColor: LABEL_GRAY_RGB, halign: 'left' as const } } : {}),
        [hasDateCol ? 3 : 2]: { fontStyle: 'bold', textColor: [0, 0, 0], halign: 'right' },
        [hasDateCol ? 4 : 3]: { textColor: [0, 0, 0], halign: 'right' },
        [hasDateCol ? 5 : 4]: { textColor: [0, 0, 0], halign: 'right' },
        ...(hasBridgingCol ? { [hasDateCol ? 6 : 5]: { textColor: [0, 0, 0] as [number, number, number], halign: 'right' as const } } : {}),
      },
      alternateRowStyles: { fillColor: ZEBRA_GRAY_RGB },
      margin: { left: MARGIN, right: MARGIN },
      tableWidth: innerW,
      showHead: 'everyPage',
      didDrawPage: (hookData) => {
        // Keep header/footer on continuation pages created by autoTable.
        // Skip the first page (already has header/footer + summary bar).
        if (hookData.pageNumber > 1) {
          addReportHeader(doc, 'Buying report');
          addFooter(doc, logoDataUrl, logoAspect, data.agentInfo);
        }
      },
    });
  }
};

// ── Estimated Payment Timeline & Affordability (landscape poster page) ─────
const renderInvestmentTimelinePage = (
  doc: jsPDF,
  data: BuyingPdfData,
  logoDataUrl?: string,
  logoAspect?: number,
) => {
  const t = data.investmentTimeline;
  if (!t) return;
  const fs = data.fundSummary;
  const isHdb = data.inputs.propertyTab === 'hdb';
  const isResale = data.inputs.propertyTab === 'private' && data.inputs.privateSubTab === 'resale';
  const simple = data.inputs.mode === 'simple';

  doc.addPage('a4', 'landscape');
  const timelinePage = doc.getCurrentPageInfo().pageNumber;
  addReportHeader(doc, isHdb ? 'HDB Purchase Timeline' : isResale ? 'Resale Purchase Timeline' : 'Project Investment Timeline');

  // Reset stroke width — the cover page's photo circle leaves it at 2mm.
  doc.setLineWidth(0.2);

  const pageWidth = doc.internal.pageSize.getWidth();    // ~297
  const pageHeight = doc.internal.pageSize.getHeight();  // ~210
  const M = 10;
  const innerW = pageWidth - M * 2;

  // ── Huttons brand palette ──
  const NAVY: [number, number, number] = [0, 31, 67];        // #001F43
  const ORANGE: [number, number, number] = [245, 130, 32];   // #F58220
  const GOLD: [number, number, number] = [201, 164, 92];     // #C9A45C
  const GREY: [number, number, number] = [120, 122, 128];
  const LIGHT_LINE: [number, number, number] = [228, 230, 235];
  const CARD_HEADER: [number, number, number] = [239, 242, 248];
  const PEACH: [number, number, number] = [253, 240, 232];   // orange tint fill
  const cur = formatCurrency;

  // ── Title block ──
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...ORANGE);
  doc.text('ASSET PROGRESSION & INVESTMENT', M, 14);
  doc.setFontSize(18);
  doc.setTextColor(...NAVY);
  doc.text('Estimated Payment Timeline & Affordability', M, 21);
  doc.setFillColor(...ORANGE);
  doc.rect(M, 23.5, 36, 0.9, 'F');
  doc.setFillColor(...GOLD);
  doc.rect(M + 37, 23.7, 12, 0.55, 'F');

  // ── KPI cards row ──
  const kpiY = 26, kpiH = 17, kpiGap = 4;
  const kpiW = (innerW - kpiGap * 3) / 4;
  // `value` may carry a second figure (`aside`) shown beside the headline one,
  // separated by a pipe — used by the loan cards to pair a quantum with its
  // instalment. Both are drawn in the headline face; the pair auto-shrinks to fit.
  const kpiCard = (
    idx: number, label: string, value: string | { main: string; aside: string }, sub: string,
    variant: 'navy' | 'accent' | 'plain',
  ) => {
    const x = M + idx * (kpiW + kpiGap);
    if (variant === 'navy') {
      doc.setFillColor(...NAVY);
      doc.roundedRect(x, kpiY, kpiW, kpiH, 1.5, 1.5, 'F');
    } else {
      doc.setDrawColor(...LIGHT_LINE);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(x, kpiY, kpiW, kpiH, 1.5, 1.5, 'FD');
      if (variant === 'accent') {
        doc.setFillColor(...ORANGE);
        doc.rect(x, kpiY, kpiW, 1.4, 'F');
      }
    }
    const valueColor: [number, number, number] = variant === 'navy'
      ? [255, 255, 255]
      : variant === 'accent' ? ORANGE : NAVY;
    const captionColor: [number, number, number] = variant === 'navy' ? [190, 198, 212] : GREY;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.4);
    doc.setTextColor(...(variant === 'navy' ? GOLD : GREY));
    doc.text(label, x + 4, kpiY + 5.7);
    doc.setFontSize(13.5);
    doc.setTextColor(...valueColor);
    if (typeof value === 'string') {
      doc.text(value, x + 4, kpiY + 11.9);
    } else {
      const sep = '  |  ';
      const avail = kpiW - 8;
      let size = 13.5;
      let wMain = 0, wSep = 0, wAside = 0;
      // Shrink both figures together until the pair clears the card padding.
      for (;;) {
        doc.setFontSize(size);
        wMain = doc.getTextWidth(value.main);
        wSep = doc.getTextWidth(sep);
        wAside = doc.getTextWidth(value.aside);
        if (wMain + wSep + wAside <= avail || size <= 7) break;
        size -= 0.25;
      }
      let vx = x + 4;
      doc.text(value.main, vx, kpiY + 11.9);
      vx += wMain;
      doc.setTextColor(...captionColor);
      doc.text(sep, vx, kpiY + 11.9);
      vx += wSep;
      doc.setTextColor(...valueColor);
      doc.text(value.aside, vx, kpiY + 11.9);
    }
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...captionColor);
    // The loan cards' caption carries the rate the instalment was computed at, which
    // can run past the card edge — step the caption down until it fits.
    let subSize = 6.4;
    doc.setFontSize(subSize);
    while (doc.getTextWidth(sub) > kpiW - 8 && subSize > 5.0) {
      subSize -= 0.1;
      doc.setFontSize(subSize);
    }
    doc.text(sub, x + 4, kpiY + 15.3);
  };

  const upfrontCash = fs ? fs.totalOutlayCpf + fs.totalOutlayCash : 0;
  kpiCard(0, isHdb || isResale ? 'PURCHASE PRICE' : 'PURCHASE PRICE (BUC)', cur(t.bucPrice),
    isHdb ? 'HDB resale flat' : isResale ? 'Completed private unit' : 'Building Under Construction', 'navy');
  kpiCard(1, 'UPFRONT', cur(upfrontCash), 'Before 1st loan drawdown', 'accent');
  // Loan cards. Detailed mode collects income, so it can contrast what the borrower
  // qualifies for (TDSR eligibility) against what this purchase actually needs (LTV),
  // each with its instalment at completion. Simple mode has no income input — fall
  // back to the plain bank-loan / instalment pair.
  // Every instalment on this page is computed at t.interestRate (the bank rate, or the
  // HDB concessionary rate for an HDB loan) — name it so the figure can be checked.
  const atRate = `@ ${formatPct(t.interestRate)} p.a.`;
  // The loan gets a box each for the amount and the instalment, so both read at full size.
  kpiCard(2, simple || t.estLtvLoan <= 0
    ? `BANK LOAN (${formatPct(t.ltvPct, 0)} LTV)`
    : `LOAN REQUIRED (BASED ON ${formatPct(t.ltvPct, 0)} LTV)`,
    cur(t.ltvLoan),
    simple || t.estLtvLoan <= 0 ? 'Est. based on IWAA' : 'Loan amount', 'plain');
  kpiCard(3, 'MONTHLY INSTALMENT', cur(fs ? fs.monthlyInstallment : 0),
    `${isResale || isHdb ? 'From completion' : 'At completion (CSC)'} ${atRate}`, 'plain');

  // ── Section header helper (numbered navy badge) ──
  // Title only: the grey one-line caption that used to sit under each title was
  // dropped so the reclaimed height can go into larger type in the sections below.
  const sectionBadgeRadius = 3.2;
  const sectionHeaderTopGap = 1.2;
  const sectionHeaderBottomGap = 0.7;
  const sectionHeadYAfter = (previousBottom: number) =>
    previousBottom + sectionBadgeRadius + sectionHeaderTopGap;
  const sectionTableYAfter = (headerY: number) =>
    headerY + sectionBadgeRadius + sectionHeaderBottomGap;
  // `note` rides on the title's baseline in smaller grey type, braced — for terms that
  // qualify the whole section (e.g. the rate and tenure the loan figures are computed at)
  // and would otherwise cost the section a table row each. Pass it unbraced.
  const sectionHead = (n: number, title: string, x: number, y: number, note?: string) => {
    doc.setFillColor(...NAVY);
    doc.circle(x + sectionBadgeRadius, y, sectionBadgeRadius, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.2);
    doc.setTextColor(255, 255, 255);
    doc.text(String(n), x + sectionBadgeRadius, y, { align: 'center', baseline: 'middle' });
    doc.setFontSize(11.5);
    doc.setTextColor(...NAVY);
    const titleX = x + sectionBadgeRadius * 2 + 3.2;
    doc.text(title, titleX, y, { baseline: 'middle' });
    if (note) {
      const titleW = doc.getTextWidth(title); // measured at the title's own size
      // A 'middle'-baselined string centres on its OWN height, so drawing the note at the
      // title's y floats small type above the title's baseline. jsPDF shifts a middle
      // baseline down by size/sf * (1.5 - lineHeightFactor); resolve the title's real
      // baseline with that and sit the note on it, so both read as one flush line.
      const baselineOf = (size: number, midY: number) =>
        midY + (size / doc.internal.scaleFactor) * (1.5 - doc.getLineHeightFactor());
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.6);
      doc.setTextColor(...GREY);
      // Braced so the terms read as an aside on the heading rather than part of the title.
      doc.text(`{${note}}`, titleX + titleW + 3.0, baselineOf(11.5, y), { baseline: 'alphabetic' });
    }
  };

  // ── Section 1: Payment Timeline ──
  const s1Y = kpiY + kpiH + 5.5;
  sectionHead(1, 'Payment Timeline', M, s1Y);

  // `due` is the total buyer-funded amount at the milestone; `cpf` is the CPF
  // portion of it (cash = due − cpf). The card renderer labels it CASH DUE /
  // CPF DUE / CASH + CPF DUE and shows the split when both are used.
  // `cpfCash` (simple mode): the milestone's funding split is unknown, so label it
  // "CPF/CASH DUE" instead of "CASH DUE". `cashSplit` (simple mode, 10% regime): show a
  // Cash line (mandatory) plus a CPF/Cash line (flexible) instead of one amount.
  type Card = { title: string; date: string; due?: number; cpf?: number; note: string; highlight?: boolean; cpfCash?: boolean; dueLabel?: string; cashSplit?: { cash: number; cpfCash: number } };
  const stampDutyTotal = t.bsd + t.absd;
  const cards: Card[] = isResale ? [
    { title: 'PAY 1% — RECEIVE OTP', date: t.bookingDate, due: t.otpAmount, cpf: t.otpCpf, note: 'Option fee (1%). Cheque / cashier order.' },
    { title: 'EXERCISE OTP — PAY 4%', date: t.exerciseDate, due: t.pay15Amount, cpf: t.pay15Cpf, note: 'Within 14 days of option.' },
    { title: 'PAY STAMP DUTY', date: t.stampDutyDate, due: stampDutyTotal, cpf: t.stampDutyCpf, note: `BSD ${cur(t.bsd)}${t.absd > 0 ? ` · ABSD ${cur(t.absd)}` : ''}.`, dueLabel: 'CASH (REIMBURSABLE FROM CPF)' },
    { title: 'COMPLETION — PAY 95%', date: t.foundationDate, due: t.foundationTopUp, cpf: t.foundationCpf, note: `Total cash/CPF ${cur(t.totalCashCpf)} · Loan ${cur(t.ltvLoan)}.`, highlight: true, cpfCash: t.simpleMode, dueLabel: t.simpleMode ? undefined : 'CASH/CPF DUE' },
  ] : isHdb ? [
    { title: 'HFE LETTER', date: t.bookingDate, note: `Est. loan ${cur(t.ltvLoan)} · Grants ${cur(t.refundable)}.` },
    { title: 'OPTION TO PURCHASE', date: t.spReceiveDate, due: t.otpAmount, cpf: t.otpCpf, note: 'Pay option fee.' },
    { title: 'REQUEST VALUATION', date: t.exerciseDate, note: 'After OTP. Valuation fee applies.' },
    { title: 'EXERCISE OTP', date: t.stampDutyDate, due: t.pay15Amount, cpf: t.pay15Cpf, note: 'Within 21 days. Exercise fee.' },
    { title: 'RESALE APPLICATION', date: t.hdbResaleAppDate ?? '', due: t.hdbResaleAppAmount, note: 'After exercise. Admin + caveat fees.' },
    { title: 'HDB ACCEPTANCE', date: t.pay15Date, due: stampDutyTotal, cpf: t.stampDutyCpf, note: `BSD ${cur(t.bsd)}${t.absd > 0 ? ` · ABSD ${cur(t.absd)}` : ''}.` },
    { title: 'COMPLETION', date: t.foundationDate, due: t.foundationTopUp, cpf: t.foundationCpf, note: `Total upfront ${cur(t.totalCashCpf)}.`, highlight: true },
  ] : [
    // Simple mode relabels the buyer-funded milestones (Booking Fee / Pay 15%) and,
    // since the cash/CPF mix is unknown, marks everything past the mandatory booking fee
    // as "CPF/CASH DUE".
    { title: t.simpleMode ? 'BOOKING FEE 5% — RECEIVE OTP' : 'PAY 5% — RECEIVE OTP', date: t.bookingDate, due: t.otpAmount, cpf: t.otpCpf, note: `On cancellation: 1.25% (${cur(t.cancellationPenalty)}) forfeited · 3.75% (${cur(t.refundable)}) refundable.` },
    { title: 'RECEIVE S&P', date: t.spReceiveDate, note: 'Within 3 days of OTP. No payment due.' },
    { title: 'EXERCISE S&P', date: t.exerciseDate, note: 'Within 21 days. Sign & exercise the option.' },
    { title: 'PAY STAMP DUTY', date: t.stampDutyDate, due: stampDutyTotal, cpf: t.stampDutyCpf, note: `Within 14 days. BSD ${cur(t.bsd)} · ABSD ${cur(t.absd)}.`, cpfCash: t.simpleMode },
    { title: 'PAY 15%', date: t.pay15Date, due: t.pay15Amount, cpf: t.pay15Cpf, note: `Completion. Cumulative 20% + SD paid: ${cur(t.sum20PlusSd)}.`, cpfCash: t.simpleMode,
      // 55% LTV / 10% regime: the S&P (15%) covers the remaining mandatory cash (5%) plus a
      // flexible 10% — show both lines so the buyer sees exactly how much must be cash.
      cashSplit: t.simpleMode && (t.pay15MandatoryCash ?? 0) > 0
        ? { cash: t.pay15MandatoryCash ?? 0, cpfCash: Math.max(0, t.pay15Amount - (t.pay15MandatoryCash ?? 0)) }
        : undefined },
    // Foundation split follows the actual schedule: 5% + 5% loan at 75% LTV, but
    // fully buyer-funded (10%) under the stretched-tenure 55% LTV regime.
    { title: (() => {
      const fdn = t.stages[0];
      const buyerPct = fdn && t.bucPrice > 0 ? Math.round(((fdn.cashCpf) / t.bucPrice) * 100) : 5;
      const loanPct = fdn ? fdn.loanPct : 5;
      return `FOUNDATION (${buyerPct}%${loanPct > 0 ? ` + ${loanPct}% loan` : ''})`;
    })(), date: t.foundationDate, due: t.foundationTopUp, cpf: t.foundationCpf, note: `Total cash/CPF outlay to date: ${cur(t.totalCashCpf)}.`, highlight: true, cpfCash: t.simpleMode },
  ];

  const cardsTop = s1Y + 5.5;
  const cardGap = 2;
  const cardW = (innerW - cardGap * (cards.length - 1)) / cards.length;
  const cardH = 30;
  const cardHeaderH = 9.4;
  cards.forEach((c, idx) => {
    const x = M + idx * (cardW + cardGap);
    if (c.highlight) {
      doc.setFillColor(...PEACH);
      doc.setDrawColor(...ORANGE);
    } else {
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(...LIGHT_LINE);
    }
    doc.roundedRect(x, cardsTop, cardW, cardH, 1.4, 1.4, 'FD');

    // Header band behind the milestone title, matching the on-screen timeline cards.
    doc.setFillColor(...(c.highlight ? PEACH : CARD_HEADER));
    doc.roundedRect(x, cardsTop, cardW, cardHeaderH, 1.4, 1.4, 'F');
    doc.rect(x, cardsTop + cardHeaderH - 1.4, cardW, 1.4, 'F');
    doc.setDrawColor(...(c.highlight ? ORANGE : LIGHT_LINE));
    doc.setLineWidth(0.1);
    doc.line(x, cardsTop + cardHeaderH, x + cardW, cardsTop + cardHeaderH);

    // number badge
    const br = 2.8;
    const bcx = x + 3.4 + br;
    doc.setFillColor(...(c.highlight ? ORANGE : NAVY));
    doc.circle(bcx, cardsTop + cardHeaderH / 2, br, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.0);
    doc.setTextColor(255, 255, 255);
    doc.text(romanNumeral(idx + 1), bcx, cardsTop + cardHeaderH / 2, {
      align: 'center',
      baseline: 'middle',
    });
    // title
    doc.setFontSize(6.2);
    doc.setTextColor(...NAVY);
    doc.text(doc.splitTextToSize(c.title, cardW - 12), bcx + br + 1.6, cardsTop + 3.9);
    // date — always an estimate, so prefix "Est." (matches the schedule table).
    // Blank when the agent hid timeline dates: leave the line empty rather than
    // print a placeholder, so every card keeps the same height.
    if (c.date) {
      doc.setFontSize(9.4);
      doc.setTextColor(...NAVY);
      doc.text(`Est. ${c.date}`, x + 3.4, cardsTop + 14.0);
    }
    // ── Amount due: split into CPF vs Cash ──
    const due = c.due ?? 0;
    const cpfDue = Math.min(c.cpf ?? 0, due);
    const cashDue = Math.max(0, due - cpfDue);
    const mixed = cpfDue > 0 && cashDue > 0;
    const dueLabel = c.dueLabel ?? (c.cashSplit
      ? 'AMOUNT DUE'
      : c.cpfCash && due > 0
      ? 'CPF/CASH DUE'
      // Simple mode: no payment at this milestone → drop the meaningless "CASH DUE" label.
      : due <= 0 ? (t.simpleMode ? '' : 'CASH DUE') : mixed ? 'CASH + CPF DUE' : cpfDue > 0 ? 'CPF DUE' : 'CASH DUE');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.3);
    doc.setTextColor(...GREY);
    if (dueLabel) doc.text(dueLabel, x + 3.4, cardsTop + 17.6);
    if (c.cashSplit) {
      // Simple mode 10% regime — mandatory Cash (orange) + flexible CPF/Cash (navy).
      // Spread the two lines (and push the hairline down below) so they don't crowd, and
      // align both amounts to a shared column past the widest label so they line up.
      doc.setFontSize(7.6);
      doc.setFont('helvetica', 'bold');
      const splitValX = x + 3.4 + doc.getTextWidth('CPF/Cash') + 2.2;
      doc.setTextColor(...ORANGE);
      doc.text('Cash', x + 3.4, cardsTop + 20.6);
      doc.text(cur(c.cashSplit.cash), splitValX, cardsTop + 20.6);
      doc.setTextColor(...NAVY);
      doc.text('CPF/Cash', x + 3.4, cardsTop + 24.2);
      doc.text(cur(c.cashSplit.cpfCash), splitValX, cardsTop + 24.2);
    } else if (mixed) {
      // Two compact lines — cash (orange) and CPF (navy) — so the split is explicit.
      doc.setFontSize(7.6);
      doc.setTextColor(...ORANGE);
      doc.text(`Cash ${cur(cashDue)}`, x + 3.4, cardsTop + 20.6);
      doc.setTextColor(...NAVY);
      doc.text(`CPF ${cur(cpfDue)}`, x + 3.4, cardsTop + 24.2);
    } else {
      doc.setFontSize(11.5);
      if (due > 0) {
        doc.setTextColor(...(cpfDue > 0 ? NAVY : ORANGE));
        doc.text(cur(due), x + 3.4, cardsTop + 23.0);
      } else {
        doc.setTextColor(188, 190, 195);
        doc.text('—', x + 3.4, cardsTop + 23.0);
      }
    }
    // hairline — sits below the tallest amount treatment (the two-line splits).
    const hairlineY = 25.6;
    doc.setDrawColor(...(c.highlight ? [242, 214, 190] as [number, number, number] : LIGHT_LINE));
    doc.setLineWidth(0.15);
    doc.line(x + 3.4, cardsTop + hairlineY, x + cardW - 3.4, cardsTop + hairlineY);
    // note — two lines at the card's foot; step the type down if it needs a third.
    doc.setFont('helvetica', 'normal');
    let noteSize = 4.8;
    doc.setFontSize(noteSize);
    let noteLines = doc.splitTextToSize(c.note, cardW - 6.5) as string[];
    while (noteLines.length > 2 && noteSize > 3.6) {
      noteSize -= 0.2;
      doc.setFontSize(noteSize);
      noteLines = doc.splitTextToSize(c.note, cardW - 6.5) as string[];
    }
    doc.setTextColor(110, 112, 118);
    doc.text(noteLines, x + 3.4, cardsTop + 27.3);
  });

  // ── Phase bands ──
  const bandY = cardsTop + cardH + 1.2;
  const bandH = 6.2;
  const splitIdx = cards.length - 1;
  const splitX = M + splitIdx * (cardW + cardGap);
  doc.setFillColor(...NAVY);
  doc.rect(M, bandY, splitX - cardGap - M, bandH, 'F');
  doc.setFillColor(...ORANGE);
  doc.rect(splitX, bandY, M + innerW - splitX, bandH, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.0);
  const band1 = isHdb ? 'HFE TO ACCEPTANCE' : isResale ? 'OPTION TO STAMP DUTY (~4 WEEKS)' : 'WITHIN 8 WEEKS OF OPTION';
  const band2 = isHdb ? 'COMPLETION' : isResale ? 'COMPLETION (~10–12 WEEKS)' : '~6 MTHS FROM LAUNCH';
  doc.text(band1, (M + splitX - cardGap) / 2, bandY + bandH / 2 + 1.4, { align: 'center' });
  doc.setFontSize(6.8);
  doc.text(band2, (splitX + M + innerW) / 2, bandY + bandH / 2 + 1.4, { align: 'center' });

  // ── Lower two columns ──
  const leftX = M;
  const colGap2 = 8;
  const leftW2 = 120;
  const rightX = M + leftW2 + colGap2;
  const rightW2 = innerW - leftW2 - colGap2;
  const lowerY = sectionHeadYAfter(bandY + bandH);
  const lowerTableY = sectionTableYAfter(lowerY);

  // Height of the brand band at the foot of the page — the columns below size their
  // type to stop clear of it, so a longer schedule shrinks rather than runs under it.
  const FOOTER_H = 22;
  const bodyBottom = pageHeight - FOOTER_H - 2.5;
  /** Height of one autoTable line box, in mm (fontSize x 1.15 / scaleFactor). */
  const lineMm = (f: number) => (f * 1.15) / 2.8346;

  const lastY = () => (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? lowerY;
  const footColLeft: CellHook = (h) => { if (h.section === 'foot' && h.column.index === 0) h.cell.styles.halign = 'left'; };
  const fbHook: CellHook = (h) => {
    if (h.section === 'foot' && h.column.index === 0) h.cell.styles.halign = 'left';
    if ((h.section === 'body' || h.section === 'foot')
      && typeof h.cell.raw === 'string' && h.cell.raw.trim().startsWith('-')) {
      h.cell.styles.textColor = ORANGE;
    }
  };

  // ---- LEFT: Section 2 Upfront Breakdown ----
  sectionHead(2, 'Upfront Breakdown', leftX, lowerY);
  // Simple mode: the buyer's cash/CPF mix is unknown, so split each row into the mandatory
  // Cash portion and a flexible CPF/Cash portion instead of the detailed CPF / Cash split.
  const ucBody: RowInput[] = (fs?.outlayRows ?? []).map((r) => {
    const cashAndBridging = r.cash + (r.bridging ?? 0);
    // Simple mode shows eligibility, not an entered funding allocation. Include both the
    // CPF and cash portions in the row total, then use mandatoryCash to separate the amount
    // that must be paid in cash from the balance that may be paid by CPF or cash.
    const full = simple ? r.cpf + cashAndBridging : cashAndBridging;
    if (simple) {
      const mand = Math.min(full, Math.max(0, r.mandatoryCash ?? 0));
      const flex = Math.max(0, full - mand);
      return [r.label, mand > 0 ? cur(mand) : '', flex > 0 ? cur(flex) : '', cur(full)];
    }
    return [r.label, r.cpf > 0 ? cur(r.cpf) : '', cur(full), cur(r.cpf + full)];
  });
  const simpleOutlayTotal = fs
    ? fs.totalOutlayCpf + fs.totalOutlayCash + (fs.totalOutlayBridging ?? 0)
    : 0;
  const simpleMandatoryCash = fs
    ? Math.min(
        simpleOutlayTotal,
        Math.max(
          0,
          fs.totalMandatoryCash
            ?? fs.outlayRows.reduce((sum, row) => sum + (row.mandatoryCash ?? 0), 0),
        ),
      )
    : 0;
  // The two left-hand tables share a type size, stepped down until the pair — plus
  // Section 3's header — clears the footer band. Section 3's row count is fixed:
  // age + employment, four income rows in detailed mode, three eligibility rows.
  const bpRowCount = 5 + (simple ? 0 : 4);
  const hasPledgeBlock = t.unpledge > 0 || t.pledge > 0;
  /** Height of the label/value pledging rows that trail Section 3, when shown. */
  const pledgeBlockH = (f: number) => (hasPledgeBlock ? 3 * (lineMm(f) + 1.0) + 1.2 : 0);
  const leftHeight = (f: number) =>
    (ucBody.length + 2) * (lineMm(f) + 1.9)                       // upfront: head + body + foot
    + (bpRowCount + 1) * (lineMm(f - 0.8) + 1.6)                  // borrower profile: head + body
    + lineMm(f - 0.8)                                             // + the wrapped "recognised income" line
    + pledgeBlockH(f - 0.8);
  let ucFont = 7.6;
  while (ucFont > 4.8 && leftHeight(ucFont) > bodyBottom - lowerTableY - 8.7) ucFont -= 0.1;
  const bpFont = ucFont - 0.8;

  const ucFoot: RowInput[] = fs ? [
    simple
      ? [
          'Total Initial Outlay',
          cur(simpleMandatoryCash),
          cur(Math.max(0, simpleOutlayTotal - simpleMandatoryCash)),
          cur(simpleOutlayTotal),
        ]
      : [
          'Total Initial Outlay',
          fs.totalOutlayCpf > 0 ? cur(fs.totalOutlayCpf) : '',
          cur(fs.totalOutlayCash + (fs.totalOutlayBridging ?? 0)),
          cur(fs.totalOutlayCpf + fs.totalOutlayCash + (fs.totalOutlayBridging ?? 0)),
        ],
  ] : [];
  autoTable(doc, {
    startY: lowerTableY,
    margin: { left: leftX, bottom: 3 },
    tableWidth: leftW2,
    theme: 'grid',
    styles: { font: 'helvetica', fontSize: ucFont, cellPadding: { top: 0.95, bottom: 0.95, left: 3, right: 3 }, lineColor: LIGHT_LINE, lineWidth: 0.1 },
    headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontSize: ucFont - 0.9, halign: 'right' },
    columnStyles: simple ? {
      0: { textColor: [60, 62, 70], halign: 'left' },
      1: { halign: 'right', fontStyle: 'bold', textColor: NAVY },
      2: { halign: 'right', fontStyle: 'bold', textColor: NAVY },
      3: { halign: 'right', fontStyle: 'bold', textColor: NAVY },
    } : {
      0: { textColor: [60, 62, 70], halign: 'left' },
      1: { halign: 'right', textColor: [95, 97, 103] },
      2: { halign: 'right', fontStyle: 'bold', textColor: NAVY },
      3: { halign: 'right', fontStyle: 'bold', textColor: NAVY },
    },
    head: [simple ? ['', 'CASH', 'CPF/CASH', 'TOTAL'] : ['', 'CPF', 'CASH', 'TOTAL']],
    body: ucBody,
    foot: ucFoot,
    footStyles: { fillColor: PEACH, textColor: ORANGE, fontStyle: 'bold', halign: 'right', fontSize: ucFont },
    didParseCell: footColLeft,
  });

  // ---- LEFT: Section 3 Loan & Eligibility ----
  const s3Y = sectionHeadYAfter(lastY());
  // The loan terms every figure in this section is computed at — an HDB loan quotes the
  // concessionary rate rather than a bank rate. They qualify the whole section, so they
  // sit in the header rather than taking two rows of the borrower table.
  const isHdbLoan = isHdb && data.inputs.hdbLoanSource === 'hdb';
  const s3Note = [
    `${isHdbLoan ? 'HDB concessionary' : 'Bank'} rate ${formatPct(t.interestRate)} p.a.`,
    t.maxLoanTenure > 0 ? `${t.maxLoanTenure} yrs tenure` : '',
  ].filter(Boolean).join('  ·  ');
  sectionHead(3, 'Loan & Eligibility', leftX, s3Y, s3Note);

  // ── Borrower Profile table: one column per borrower + a Total column ──
  // Falls back to ageIncomeRows when the caller doesn't supply full profiles.
  const borrowers: TimelineBorrower[] = t.borrowers?.length
    ? t.borrowers
    : t.ageIncomeRows.map((r) => ({
        age: r.age, selfEmployed: false, income: r.salary, recognisedIncome: r.salary,
        otherAnnualIncome: 0, existingMonthlyDebt: 0, monthlyCpfOa: 0,
      }));
  const nB = borrowers.length;
  const multi = nB > 1;
  // Single borrower: the borrower column IS the total, so drop the Total column.
  const bpCols = nB + (multi ? 2 : 1); // attribute + borrowers (+ total when joint)
  const dash = '–';
  const sumBy = (f: (b: TimelineBorrower) => number) => borrowers.reduce((a, b) => a + f(b), 0);
  // Income cell shows the recognised figure beneath the gross when haircuts apply.
  const incomeCell = (b: TimelineBorrower) =>
    b.income > 0 && b.recognisedIncome !== b.income
      ? `${cur(b.income)}\n(recognised ${cur(Math.round(b.recognisedIncome))})`
      : cur(b.income);
  const hlFill = [235, 238, 244] as [number, number, number];
  const spanRow = (label: string, value: string): RowInput => [
    { content: label, styles: { fontStyle: 'bold' as const, textColor: NAVY, fillColor: hlFill } },
    { content: value, colSpan: bpCols - 1, styles: { halign: 'right' as const, fontStyle: 'bold' as const, textColor: ORANGE, fillColor: hlFill } },
  ];
  // Highlight row with one value per borrower + orange total (joint purchases with
  // per-borrower eligibility figures).
  const hlRow = (label: string, perBorrower: string[], total: string): RowInput => [
    { content: label, styles: { fontStyle: 'bold' as const, textColor: NAVY, fillColor: hlFill } },
    ...perBorrower.map((v) => ({ content: v, styles: { halign: 'right' as const, fontStyle: 'bold' as const, textColor: NAVY, fillColor: hlFill } })),
    { content: total, styles: { halign: 'right' as const, fontStyle: 'bold' as const, textColor: ORANGE, fillColor: hlFill } },
  ];
  // Total-column cells (appended only for joint purchases).
  const totCell = (v: string): string[] => (multi ? [v] : []);
  // Monthly installment on the eligible loan quantum, at the quoted loan terms below.
  const instOn = (loan: number) => cur(Math.round(calcMonthlyInstallment(loan, t.interestRate, t.maxLoanTenure)));
  // Per-borrower eligibility rows only when every borrower carries the figures
  // (detailed joint purchases); otherwise the totals span the row as before.
  const perBorrowerElig = multi && borrowers.every((b) => b.loanEligibility !== undefined);
  const eligRows: RowInput[] = perBorrowerElig
    ? [
        hlRow('Loan Eligibility', borrowers.map((b) => cur(b.loanEligibility ?? 0)), cur(t.estLtvLoan)),
        hlRow('Max Purchase Price', borrowers.map((b) => cur(b.maxPurchasePrice ?? 0)), cur(t.maxPropertyPrice)),
        hlRow('Est. Monthly Installment', borrowers.map((b) => instOn(b.loanEligibility ?? 0)), instOn(t.estLtvLoan)),
      ]
    : [
        spanRow('Loan Eligibility', cur(t.estLtvLoan)),
        spanRow('Max Purchase Price', cur(t.maxPropertyPrice)),
        spanRow('Est. Monthly Installment', instOn(t.estLtvLoan)),
      ];
  // Simple mode doesn't collect income / debt / CPF, so those rows would all read $0 —
  // omit them and show only the age / employment basics alongside the eligibility figures.
  const bpBody: RowInput[] = [
    ['Age', ...borrowers.map((b) => `${b.age} yrs`), ...totCell(`${t.iwaa} yrs (IWAA)`)],
    ['Employment Status', ...borrowers.map((b) => (b.selfEmployed ? 'Self-employed' : 'Employed')), ...totCell(dash)],
    ...(simple ? [] : [
      ['Income', ...borrowers.map(incomeCell), ...totCell(`${cur(t.totalIncome ?? sumBy((b) => b.recognisedIncome))}\n(recognised)`)],
      ['Other Annual Income', ...borrowers.map((b) => cur(b.otherAnnualIncome)), ...totCell(cur(sumBy((b) => b.otherAnnualIncome)))],
      ['Existing Monthly Debt', ...borrowers.map((b) => cur(b.existingMonthlyDebt)), ...totCell(cur(t.totalMonthlyDebt))],
      ['Monthly CPF Contribution', ...borrowers.map((b) => cur(b.monthlyCpfOa)), ...totCell(cur(t.monthlyCpfOa))],
    ] as RowInput[]),
    ...eligRows,
  ];
  const attrW = 34;
  const totW = multi ? 24 : 0;
  const borW = (leftW2 - attrW - totW) / nB;
  autoTable(doc, {
    startY: sectionTableYAfter(s3Y),
    margin: { left: leftX, bottom: 3 },
    tableWidth: leftW2,
    theme: 'grid',
    styles: { font: 'helvetica', fontSize: bpFont, cellPadding: { top: 0.8, bottom: 0.8, left: 2, right: 2 }, lineColor: LIGHT_LINE, lineWidth: 0.1, valign: 'middle' },
    headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontSize: bpFont - 0.5, halign: 'right', cellPadding: { top: 1.0, bottom: 1.0, left: 2, right: 2 } },
    columnStyles: {
      0: { textColor: GREY, halign: 'left', cellWidth: attrW },
      ...Object.fromEntries(borrowers.map((_, i) => [i + 1, { halign: 'right' as const, fontStyle: 'bold' as const, textColor: NAVY, cellWidth: borW }])),
      ...(multi ? { [nB + 1]: { halign: 'right' as const, fontStyle: 'bold' as const, textColor: NAVY, cellWidth: totW } } : {}),
    },
    head: [[
      'BORROWER PROFILE',
      // Profile names when loaded; generic "Borrower N" otherwise.
      ...borrowers.map((b, i) => (b.name?.trim() ? b.name.trim().toUpperCase() : multi ? `BORROWER ${i + 1}` : 'BORROWER')),
      ...(multi ? ['TOTAL'] : []),
    ]],
    body: bpBody,
    didParseCell: (h) => {
      if (h.section === 'head' && h.column.index === 0) h.cell.styles.halign = 'left';
      if (h.section === 'body' && (h.cell.raw === cur(0) || h.cell.raw === dash)) h.cell.styles.textColor = [165, 167, 172];
    },
  });

  // ── Fund Pledging (when used) — compact label/value rows ──
  const groups: { title: string; rows: [string, string][] }[] = [
    ...(t.unpledge > 0 || t.pledge > 0 ? [{ title: 'FUND PLEDGING / UNPLEDGING', rows: [
      ['Unpledge (fund before 1st drawdown)', cur(t.unpledge)],
      ['Pledge (48 months)', cur(t.pledge)],
    ] as [string, string][] }] : []),
    ...(fs?.specialBridging ? [{ title: 'SPECIAL BRIDGING LOAN (EC DPS)', rows: [
      ['HDB valuation × 90%', cur(Math.round(fs.specialBridging.valuation * 0.9))],
      ['− Outstanding HDB loan', cur(fs.specialBridging.outstandingLoan)],
      ['− Resale levy', cur(fs.specialBridging.levy)],
      ['= Max special bridging loan', cur(fs.specialBridging.maxLoan)],
      ['Applied at TOP (reduces bank loan)', cur(fs.specialBridging.applied)],
    ] as [string, string][] }] : []),
  ];
  let ly = lastY() + 1.6;
  const rowH = lineMm(bpFont) + 1.0;
  groups.forEach((g) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(bpFont - 0.4);
    doc.setTextColor(...ORANGE);
    doc.text(g.title, leftX, ly + rowH - 1.1);
    ly += rowH + 0.2;
    g.rows.forEach(([label, value]) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(bpFont);
      doc.setTextColor(...GREY);
      doc.text(label, leftX, ly + rowH - 0.9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...(value === cur(0) ? [165, 167, 172] as [number, number, number] : NAVY));
      doc.text(value, leftX + leftW2, ly + rowH - 0.9, { align: 'right' });
      doc.setDrawColor(...LIGHT_LINE);
      doc.setLineWidth(0.12);
      doc.line(leftX, ly + rowH, leftX + leftW2, ly + rowH);
      ly += rowH;
    });
    ly += 0.6;
  });

  // ---- RIGHT: Section 4 Progressive Payment Schedule ----
  doc.setPage(timelinePage);
  sectionHead(4, isHdb ? 'HDB Payment Schedule' : isResale ? 'Resale Payment Schedule' : 'Progressive Payment Schedule',
    rightX, lowerY);
  // Detailed mode splits the buyer-funded portion into CASH and CPF (mirrors the
  // on-screen Progressive Schedule); simple mode keeps the combined column since
  // the buyer's cash/CPF mix is unknown.
  const stageCpf = (s: InvestmentTimelineStage) => Math.min(s.cpf ?? 0, s.cashCpf);
  // The buyer-funded portion of every row is split into two columns:
  //   detailed mode — CASH / CPF (the entered funding allocation);
  //   simple mode   — CASH / CPF/CASH, mirroring the Upfront Breakdown: only the
  //   mandatory-cash portion (booking fee, and any 10%-regime S&P cash) sits under
  //   CASH; the flexible remainder is CPF/CASH since the buyer's mix is unknown.
  const clampCpf = (cpf: number | undefined, amount: number) => Math.min(cpf ?? 0, amount);
  // Upfront milestones (OTP → stamp duty → 15%) lead the table so it mirrors the
  // full on-screen timeline; they draw no loan.
  // HDB/resale schedules already carry their milestones inside t.stages.
  const pay15Mand = Math.min(t.pay15MandatoryCash ?? 0, t.pay15Amount);
  const milestones = isHdb || isResale ? [] : simple ? [
    { label: 'BOOKING FEE 5% (OTP)', period: t.bookingDate, cash: t.otpAmount, other: 0 },
    { label: 'PAY STAMP DUTY', period: t.stampDutyDate, cash: 0, other: stampDutyTotal },
    { label: 'PAY 15%', period: t.pay15Date, cash: pay15Mand, other: t.pay15Amount - pay15Mand },
  ] : [
    { label: 'OTP (OPTION FEE 5%)', period: t.bookingDate, cash: t.otpAmount - clampCpf(t.otpCpf, t.otpAmount), other: clampCpf(t.otpCpf, t.otpAmount) },
    { label: 'PAY STAMP DUTY', period: t.stampDutyDate, cash: stampDutyTotal - clampCpf(t.stampDutyCpf, stampDutyTotal), other: clampCpf(t.stampDutyCpf, stampDutyTotal) },
    { label: 'PAY (15%)', period: t.pay15Date, cash: t.pay15Amount - clampCpf(t.pay15Cpf, t.pay15Amount), other: clampCpf(t.pay15Cpf, t.pay15Amount) },
  ];
  const stageSplit = (s: InvestmentTimelineStage) =>
    simple ? { cash: 0, other: s.cashCpf } : { cash: s.cashCpf - stageCpf(s), other: stageCpf(s) };
  // Rows are kept numeric until after the merge so grouped milestones can be summed.
  // Loan columns are null on the upfront milestones (the bank draws nothing yet) and
  // print as a dash.
  type ProgRow = {
    label: string;
    period: string;
    cash: number;
    other: number;
    loanPct: number | null;
    loanAmount: number | null;
    repayment: number | null;
  };
  const progRowsRaw: ProgRow[] = [
    ...milestones.map((m) => ({
      label: m.label, period: m.period, cash: m.cash, other: m.other,
      loanPct: null, loanAmount: null, repayment: null,
    })),
    ...t.stages.map((s) => {
      const sp = stageSplit(s);
      return {
        label: (s.bridging ?? 0) > 0 ? `${s.label} (+ SBL ${cur(s.bridging ?? 0)})` : s.label,
        period: s.periodLabel, cash: sp.cash, other: sp.other,
        loanPct: s.loanPct, loanAmount: s.loanAmount, repayment: s.monthlyRepayment,
      };
    }),
  ];
  // Milestones merged on the on-screen Progressive Schedule collapse into one row here:
  // cash, CPF and loan sum across the group, the period and label follow its first
  // stage, and the monthly repayment is the one in force after the last drawdown in it.
  const progGroups = validScheduleGroups(data.scheduleGroups, progRowsRaw.length);
  const progRowsMerged: ProgRow[] = progGroups
    ? progGroups.map((g) => {
        const members = g.map((i) => progRowsRaw[i]);
        const first = members[0];
        const withLoan = members.filter((r) => r.loanAmount !== null);
        return {
          label: g.length > 1 ? `${first.label} + ${g.length - 1} MORE` : first.label,
          period: first.period,
          cash: members.reduce((a, r) => a + r.cash, 0),
          other: members.reduce((a, r) => a + r.other, 0),
          loanPct: withLoan.length > 0 ? withLoan.reduce((a, r) => a + (r.loanPct ?? 0), 0) : null,
          loanAmount: withLoan.length > 0 ? withLoan.reduce((a, r) => a + (r.loanAmount ?? 0), 0) : null,
          repayment: withLoan.length > 0 ? withLoan[withLoan.length - 1].repayment : null,
        };
      })
    : progRowsRaw;
  const progBody = progRowsMerged.map((r) => [
    r.label, r.period, cur(r.cash), cur(r.other),
    r.loanPct === null ? dash : formatPct(r.loanPct, 0),
    r.loanAmount === null ? dash : cur(r.loanAmount),
    r.repayment === null ? dash : cur(r.repayment),
  ]);
  // The caller blanks every period label when the agent hid timeline dates — drop the
  // column and give its width to the stage names rather than print an empty column.
  const hasPeriodCol = progBody.some((r) => String(r[1] ?? '').trim() !== '');
  const progRows = hasPeriodCol ? progBody : progBody.map((r) => [r[0], ...r.slice(2)]);
  const totLoan = t.stages.reduce((a, s) => a + s.loanAmount, 0);
  const hasLoanDisbursement = totLoan > 0;
  const totCash = t.stages.reduce((a, s) => a + stageSplit(s).cash, 0) + milestones.reduce((a, m) => a + m.cash, 0);
  const totOther = t.stages.reduce((a, s) => a + stageSplit(s).other, 0) + milestones.reduce((a, m) => a + m.other, 0);
  // Sections 4 and 5 share this column, so they share one row height: the space left
  // above the footer band, split across both tables' rows. A long schedule then shrinks
  // itself instead of pushing Section 5 off the page. Section 5's row count is known
  // ahead of its body — head + Available Funds + Initial Outlay + Balance, plus the
  // progressive down payment and pledged-funds rows when they apply.
  const fbRowCount = fs && !simple
    ? 4 + ((fs.progDpCpf > 0 || fs.progDpCash > 0) ? 1 : 0)
        + ((fs.pledged + fs.unpledged) > 0 ? 1 : 0)
    : 0;
  // Sec 4's header + total row, and (when shown) Sec 5's gap and header.
  const rightFixed = 6.5 + 5.0 + (fbRowCount > 0 ? 9.0 : 0);
  const rightRowH = Math.min(5.2, Math.max(2.9,
    (bodyBottom - lowerTableY - rightFixed) / Math.max(1, progBody.length + fbRowCount)));
  const schedRowH = rightRowH;
  // Type is sized to whatever height each row got, so short schedules print large and
  // long ones still fit. autoTable's line box is fontSize × 1.15 / 2.8346 mm; the
  // constant covers the vertical cell padding below.
  const schedFont = Math.min(7.2, Math.max(4.8, (rightRowH - 1.1) / 0.4057));
  autoTable(doc, {
    startY: lowerTableY,
    margin: { left: rightX, right: M, bottom: 3 },
    tableWidth: rightW2,
    theme: 'grid',
    styles: { font: 'helvetica', fontSize: schedFont, cellPadding: { top: 0.55, bottom: 0.55, left: 1.4, right: 1.4 }, lineColor: LIGHT_LINE, lineWidth: 0.1, overflow: 'linebreak', valign: 'middle', minCellHeight: schedRowH },
    headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontSize: 6.0, halign: 'center', valign: 'middle', minCellHeight: 6.5 },
    columnStyles: {
      // PERIOD is wide enough to hold "60 mths - Sept 31" on one line: a wrap there
      // doubles every row's height and pushes Section 5 under the footer band.
      0: { fontStyle: 'bold', textColor: NAVY, cellWidth: hasPeriodCol ? (hasLoanDisbursement ? 52 : 56) : 81, overflow: 'hidden' },
      ...(hasPeriodCol ? { 1: { textColor: GREY, cellWidth: hasLoanDisbursement ? 25 : 26 } } : {}),
      [hasPeriodCol ? 2 : 1]: { halign: 'right', cellWidth: hasPeriodCol && !hasLoanDisbursement ? 18 : 14 },
      [hasPeriodCol ? 3 : 2]: { halign: 'right', cellWidth: 14 },
      [hasPeriodCol ? 4 : 3]: { halign: 'center', cellWidth: hasPeriodCol && !hasLoanDisbursement ? 9 : 10 },
      // Wide enough for totals such as "$1,350,000" without wrapping.
      [hasPeriodCol ? 5 : 4]: { halign: 'right', cellWidth: hasPeriodCol && !hasLoanDisbursement ? 10 : 18 },
      [hasPeriodCol ? 6 : 5]: { halign: 'right', fontStyle: 'bold', textColor: NAVY, cellWidth: hasPeriodCol ? 16 : 14 },
    },
    head: [[
      'STAGE',
      ...(hasPeriodCol ? [isHdb || isResale ? 'DATE (EST.)' : 'PERIOD (EST.)'] : []),
      'CASH ($)', simple ? 'CPF/CASH ($)' : 'CPF ($)',
      'LOAN\n(%)', 'LOAN ($)', 'MONTHLY\nREPAYMENT ($)',
    ]],
    body: progRows,
    foot: [['Total', ...(hasPeriodCol ? [''] : []), cur(totCash), cur(totOther), '', cur(totLoan), '']],
    footStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: schedFont, halign: 'right', minCellHeight: 5.0 },
    didParseCell: (h) => {
      if (h.section === 'foot' && h.column.index === 0) h.cell.styles.halign = 'left';
      const cashCols = hasPeriodCol ? [2, 3] : [1, 2];
      if (h.section === 'body' && cashCols.includes(h.column.index) && h.cell.raw === cur(0)) h.cell.styles.textColor = [175, 177, 182];
      // Milestone rows draw no loan — mute their dash placeholders.
      if (h.section === 'body' && h.cell.raw === dash) h.cell.styles.textColor = [175, 177, 182];
    },
  });

  // ---- RIGHT: Section 5 Fund Balance & Affordability ----
  if (fs && !simple) {
    const s5Y = sectionHeadYAfter(lastY());
    sectionHead(5, 'Fund Balance & Affordability', rightX, s5Y);
    const fbBody: RowInput[] = [
      ['Available Funds', fs.hasCpfCol ? cur(fs.availableCpf) : '', cur(fs.availableCash)],
      ['Less: Initial Outlay', fs.hasCpfCol ? `-${cur(fs.totalOutlayCpf)}` : '', `-${cur(fs.totalOutlayCash)}`],
      ...((fs.progDpCpf > 0 || fs.progDpCash > 0)
        ? [['Less: Progressive Down Payment', fs.hasCpfCol ? `-${cur(fs.progDpCpf)}` : '', `-${cur(fs.progDpCash)}`]]
        : []),
      // Assets committed / locked to bridge the loan shortfall — cash column only,
      // matching the on-screen Fund Balance row (label follows the pledged/unpledged mix).
      ...((fs.pledged + fs.unpledged) > 0
        ? [[
            fs.unpledged <= 0 ? 'Less: Pledged Funds'
              : fs.pledged <= 0 ? 'Less: Unpledged Funds' : 'Less: Pledged & Unpledged Funds',
            fs.hasCpfCol ? '' : '',
            `-${cur(fs.pledged + fs.unpledged)}`,
          ]]
        : []),
    ];
    // Same row height as the schedule above (see rightRowH), so both tables read as a pair.
    const fbFont = Math.min(7.0, Math.max(4.8, (rightRowH - 1.5) / 0.4057));
    autoTable(doc, {
      startY: sectionTableYAfter(s5Y),
      margin: { left: rightX, right: M, bottom: 3 },
      tableWidth: rightW2,
      theme: 'grid',
      styles: { font: 'helvetica', fontSize: fbFont, cellPadding: { top: 0.75, bottom: 0.75, left: 3, right: 3 }, lineColor: LIGHT_LINE, lineWidth: 0.1 },
      headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontSize: fbFont - 0.6, halign: 'right' },
      columnStyles: {
        0: { textColor: [60, 62, 70], halign: 'left' },
        1: { halign: 'right' },
        2: { halign: 'right', fontStyle: 'bold' },
      },
      head: [['', fs.hasCpfCol ? 'CPF' : '', 'CASH']],
      body: fbBody,
      foot: [['Balance', fs.hasCpfCol ? signedCurrency(fs.balanceCpf) : '', signedCurrency(fs.balanceCash)]],
      footStyles: { fillColor: [235, 238, 244], textColor: NAVY, fontStyle: 'bold', halign: 'right', fontSize: fbFont },
      didParseCell: fbHook,
    });
    // (Monthly Instalment strip removed — the figure is already shown in the KPI
    // card at the top of the page.)
  }

  // ── Custom footer: brand band + "how long your funds last" reserve boxes ──
  const footerH = FOOTER_H;
  const footerY = pageHeight - footerH;
  doc.setFillColor(...NAVY);
  doc.rect(0, footerY, pageWidth, footerH, 'F');
  if (logoDataUrl && logoAspect) {
    const logoH = 8.5;
    const logoW = logoH * logoAspect;
    doc.addImage(logoDataUrl, 'PNG', M, footerY + (footerH - logoH) / 2 - 1.5, logoW, logoH);
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.setTextColor(255, 255, 255);
    doc.text('Huttons', M, footerY + footerH / 2 + 1);
  }
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.2);
  doc.setTextColor(190, 198, 212);
  doc.text("Singapore's Largest", M + 39, footerY + footerH / 2 - 2.5);
  doc.text('Private Real Estate Agency', M + 39, footerY + footerH / 2 + 1.4);

  if (fs && !simple) {
    const fmtM = (m: number | null) => (m === null ? '—' : m === Infinity ? 'Sustainable' : `${m} mths`);
    // Boxes sit in the right third so the centred page number stays clear of them.
    const rbW = 55, rbH = 15, rbGap = 4.5;
    const rb2X = pageWidth - M - rbW;
    const rb1X = rb2X - rbGap - rbW;
    // "How long your funds last" caption, centred above the two boxes.
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.4);
    doc.setTextColor(198, 168, 92);
    doc.text('HOW LONG YOUR FUNDS LAST', (rb1X + rb2X + rbW) / 2, footerY + 4.4, { align: 'center' });
    const drawRb = (bx: number, title: string, val: string, sub: string, valColor: [number, number, number]) => {
      const cy = footerY + 5.8;
      doc.setDrawColor(...ORANGE);
      doc.setLineWidth(0.45);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(bx, cy, rbW, rbH, 1.5, 1.5, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.0);
      doc.setTextColor(90, 92, 98);
      doc.text(title, bx + rbW / 2, cy + 4.1, { align: 'center' });
      doc.setFontSize(12.5);
      doc.setTextColor(...valColor);
      doc.text(val, bx + rbW / 2, cy + 9.9, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(4.6);
      doc.setTextColor(130, 132, 138);
      const subLines = doc.splitTextToSize(sub, rbW - 4) as string[];
      doc.text(subLines, bx + rbW / 2, cy + (subLines.length > 1 ? 12.2 : 13.1), { align: 'center' });
    };
    drawRb(rb1X, 'IF YOU STOP WORKING TODAY', fmtM(fs.monthsCurrent),
      'Leftover cash only — no further income counted.', NAVY);
    drawRb(rb2X, 'IF YOU KEEP WORKING', fmtM(fs.monthsEmployed),
      `Same funds plus ${cur(fs.monthlyCpfOa)}/mo of CPF (OA) contributions.`, ORANGE);
  }
};

// ── Input Parameters (landscape, single page) ──────────────────────────────
const renderInputsPage = (
  doc: jsPDF,
  data: BuyingPdfData,
  outlay: OutlayResult,
  logoDataUrl?: string,
  logoAspect?: number,
) => {
  doc.addPage('a4', 'landscape');
  addReportHeader(doc, 'Input Parameters');
  addFooter(doc, logoDataUrl, logoAspect, data.agentInfo);
  doc.setLineWidth(0.2);

  const i = data.inputs;
  const r = data.results;
  const joint = i.includeAdditionalBorrower;

  // Navy summary bar (same as the details page) for a consistent header.
  const yTop = drawFourColumnSummaryBar(doc, data, outlay, 13);
  doc.setLineWidth(0.2);

  const M = 12;
  const pageWidth = doc.internal.pageSize.getWidth();
  const gap = 6;
  const colW = (pageWidth - M * 2 - gap * 2) / 3;
  const col1 = M, col2 = M + colW + gap, col3 = M + (colW + gap) * 2;
  const lastY = () => (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? yTop;

  const sectionTable = (
    title: string, body: string[][], x: number, startY: number,
    opts?: { labelFrac?: number; didParseCell?: CellHook },
  ) => {
    autoTable(doc, {
      startY,
      margin: { left: x },
      tableWidth: colW,
      theme: 'grid',
      styles: { font: 'helvetica', fontSize: 7, cellPadding: { top: 2, bottom: 2, left: 3, right: 3 }, lineColor: [225, 228, 232], lineWidth: 0.1 },
      headStyles: { fillColor: SECTION_HEADER_BLUE_RGB, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7.5, halign: 'left' },
      columnStyles: {
        0: { textColor: LABEL_GRAY_RGB as [number, number, number], cellWidth: colW * (opts?.labelFrac ?? 0.55) },
        1: { fontStyle: 'bold', halign: 'right', textColor: [0, 0, 0] as [number, number, number] },
      },
      head: [[{ content: title.toUpperCase(), colSpan: 2, styles: { halign: 'left' as const } }]],
      body,
      didParseCell: opts?.didParseCell,
    });
  };

  // ── Build rows (mirrors the details page) ──
  const effCitizenship = getEffectiveCitizenshipForAbsd(i.citizenship, i.ftaEligible);
  const propLoanRows: string[][] = [
    ['Property Type', propertyTypeLabel(i)],
    ['Purchase Price', formatCurrency(i.purchasePrice)],
    ['Citizenship', CITIZENSHIP_LABELS[i.citizenship] + (i.ftaEligible ? ' (FTA)' : '')],
    ['Property Count', PROPERTY_COUNT_LABELS[i.propertyCount]],
    ['Bank Loan', i.hasBankLoan ? `Yes (LTV ${outlay.ltvPct}%)` : 'No'],
    ['Loan Amount', formatCurrency(outlay.totalLoan)],
    ['Interest Rate', `${formatPct(i.interestRate)} p.a.`],
    ['Loan Tenure', `${i.tenure} years`],
    // Blank when the agent hid timeline dates — drop the row rather than show an empty value.
    ...(i.spDate ? [[i.propertyTab === 'hdb' ? 'OTP Date' : 'OTP / S&P Date', i.spDate]] : []),
  ];
  if (effCitizenship !== i.citizenship) {
    propLoanRows.splice(4, 0, ['Effective Citizenship (ABSD)', CITIZENSHIP_LABELS[effCitizenship]]);
  }

  const shortfallRow: string[] = ['Loan Shortfall', r.loanShortfall > 0 ? formatCurrency(r.loanShortfall) : 'None'];
  const affordabilityRows: string[][] = [
    ['Max Loan Quantum', formatCurrency(r.maxLoan)],
    ['Max Purchase Price (by Loan)', r.maxPriceLoan > 0 ? formatCurrency(r.maxPriceLoan) : '–'],
    ['Required Loan', formatCurrency(r.derivedLoan)],
    shortfallRow,
    ['Total Recognised Income (Monthly)', formatCurrency(r.totalRecognisedIncome)],
    ['Existing Monthly Debt', formatCurrency(r.totalExistingDebt)],
    ['Available TDSR (Monthly)', formatCurrency(r.availableTDSR)],
    [i.propertyTab === 'hdb' && i.hdbLoanSource === 'hdb' ? 'HDB Interest Rate Floor' : 'Stress Test Rate', `${formatPct(r.stressTestRate)} p.a.`],
  ];
  const affordabilityHook: CellHook = (h) => {
    if (h.section === 'body' && h.column.index === 1 && h.row.index === 3 && r.loanShortfall > 0) {
      h.cell.styles.textColor = ALERT_RED_RGB;
    }
  };

  const stampRows: string[][] = [['BSD (Buyer\'s Stamp Duty)', formatCurrency(outlay.bsd)]];
  stampRows.push(outlay.absdAmount > 0 ? [`ABSD (${outlay.absdRatePct}%)`, formatCurrency(outlay.absdAmount)] : ['ABSD', 'None']);
  stampRows.push(['Total Stamp Duty', formatCurrency(outlay.bsd + outlay.absdAmount)]);
  stampRows.push(['Legal / Conveyancing Fee', formatCurrency(i.legalFee)]);

  // ── Column 1: Property & Loan Details ──
  sectionTable('Property & Loan Details', propLoanRows, col1, yTop);

  // ── Column 2: Borrower(s) ──
  if (joint) {
    const primaryLabel = data.primaryBorrowerName?.trim() || 'Primary';
    const additionalLabel = data.additionalBorrowerName?.trim() || 'Additional';
    autoTable(doc, {
      startY: yTop,
      margin: { left: col2 },
      tableWidth: colW,
      theme: 'grid',
      styles: { font: 'helvetica', fontSize: 6.6, cellPadding: { top: 2, bottom: 2, left: 2.5, right: 2.5 }, lineColor: [225, 228, 232], lineWidth: 0.1 },
      headStyles: { fillColor: SECTION_HEADER_BLUE_RGB, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 6.6 },
      columnStyles: {
        0: { textColor: LABEL_GRAY_RGB as [number, number, number], halign: 'left' },
        1: { fontStyle: 'bold', halign: 'right', textColor: [0, 0, 0] as [number, number, number] },
        2: { fontStyle: 'bold', halign: 'right', textColor: [0, 0, 0] as [number, number, number] },
      },
      head: [[
        { content: 'BORROWER', styles: { halign: 'left' as const } },
        { content: primaryLabel, styles: { halign: 'right' as const } },
        { content: additionalLabel, styles: { halign: 'right' as const } },
      ]],
      body: [
        ['Age', `${i.primary.age} yrs`, `${i.additional.age} yrs`],
        ['Monthly Income', formatCurrency(i.primary.monthlyIncome), formatCurrency(i.additional.monthlyIncome)],
        ['Other Annual Income', formatCurrency(i.primary.otherAnnualIncome), formatCurrency(i.additional.otherAnnualIncome)],
        ['Self-Employed', i.primary.isSelfEmployed ? 'Yes' : 'No', i.additional.isSelfEmployed ? 'Yes' : 'No'],
        ['Existing Monthly Debt', formatCurrency(i.primary.existingMonthlyDebt), formatCurrency(i.additional.existingMonthlyDebt)],
      ],
    });
  } else {
    sectionTable('Borrower', [
      ['Age', `${i.primary.age} years`],
      ['Monthly Income', formatCurrency(i.primary.monthlyIncome)],
      ['Other Annual Income', formatCurrency(i.primary.otherAnnualIncome)],
      ['Self-Employed', i.primary.isSelfEmployed ? 'Yes' : 'No'],
      ['Existing Monthly Debt', formatCurrency(i.primary.existingMonthlyDebt)],
    ], col2, yTop);
  }

  // ── Column 3: Loan Affordability + Stamp Duty & Legal ──
  sectionTable('Loan Affordability', affordabilityRows, col3, yTop, { labelFrac: 0.62, didParseCell: affordabilityHook });
  sectionTable('Stamp Duty & Legal Fees', stampRows, col3, lastY() + 5, { labelFrac: 0.6 });
};

// ── Export filename: "<profile name> - Buying Calculator - <date time>.pdf" ─
// Falls back to the borrower names when no profile is loaded.
const sanitizeFilePart = (name?: string): string =>
  (name ?? '').trim().replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();

const buildExportFilename = (data: BuyingPdfData): string => {
  const borrowers = [
    sanitizeFilePart(data.primaryBorrowerName),
    ...(data.inputs.includeAdditionalBorrower ? [sanitizeFilePart(data.additionalBorrowerName)] : []),
  ].filter(Boolean).join(' & ');
  const who = sanitizeFilePart(data.profileName) || borrowers;
  // ":" is not filename-safe (Windows) — use dots in the time instead.
  const ts = new Date().toLocaleString('en-SG', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }).replace(/:/g, '.');
  return `${[who, 'Buying Calculator', ts].filter(Boolean).join(' - ')}.pdf`;
};

// ── Public API ───────────────────────────────────────────────────────────
export const exportBuyingToPDF = async (
  data: BuyingPdfData,
  filename?: string,
): Promise<void> => {
  // Landscape throughout — the cover leads into the landscape timeline & inputs pages.
  // compress: the Huttons logo is a 4210x1035 PNG that jsPDF embeds as raw RGBA —
  // 17 MB of a 17 MB report. Flate-compressing the streams brings the same file to ~50 KB.
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4', compress: true });

  let logoDataUrl: string | undefined;
  let logoAspect: number | undefined;
  try {
    const logoImg = await loadImageEl(HuttonsWhiteLogo);
    logoDataUrl = HuttonsWhiteLogo;
    logoAspect = logoImg.width / logoImg.height;
  } catch { /* fallback to text */ }

  await renderCoverPage(doc, data, logoDataUrl, logoAspect);
  // TEMPORARILY HIDDEN — details pages (Property/Loan details, Initial Outlay, Schedule)
  // and the Input Parameters page. Re-enable by uncommenting the lines below; both need
  // `const outlay = computeOutlay(data);` back above this block.
  // renderDetailsPages(doc, data, outlay, logoDataUrl, logoAspect);
  renderInvestmentTimelinePage(doc, data, logoDataUrl, logoAspect);
  // renderInputsPage(doc, data, outlay, logoDataUrl, logoAspect);

  // Page count is only known after rendering (long schedules can spill pages).
  stampPageNumbers(doc);

  await savePdfDocument(doc, filename ?? buildExportFilename(data));
};
